import { useState, useEffect, useCallback, useMemo, useRef, type FormEvent, type ReactNode } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  runTransaction,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { masterDb } from '../../firebase/master';
import { renewalBadge, computeRenewalEnd, addOneYear } from '../../utils/renewal';
import { createFirebaseAuthUser } from '../../utils/firebaseUser';
import {
  billingFor, stageFor, paymentBreakdown, includedUsersOf, firstYearAmountOf, renewalAmountOf, extraUserRateOf,
  PAYMENT_KIND_LABEL, STAGE_LABEL, DEFAULT_INCLUDED_USERS,
  BILLING_CURRENCY_CODES, currencyLabel, currencyOfCountry, planFor, PLAN_EUR, DEFAULT_RATES_TO_EUR,
  billingCurrencyOf, paymentCurrencyOf, fmtBill, fmtBillMulti, toEur, eurApprox, curSymbol, fetchLiveRates,
  type Billing, type RatesToEur,
} from '../../utils/billing';
import AdminLayout, { type AdminTab } from './AdminLayout';
import { Modal, ConfirmModal } from '../../components/ui/Modal';
import { resetTwoStep, functionErrorText } from '../../utils/adminFunctions';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Paging } from '../../components/ui/Paging';
import { usePaging } from '../../hooks/usePaging';
import { useFitHeight } from '../../hooks/useFitHeight';
import { useColumnFilters, type FilterColumn } from '../../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../../components/ColumnFilter';
import { EntityCombobox } from '../../components/ui/EntityCombobox';
import { OptionCombobox } from '../../components/ui/OptionCombobox';
import { COUNTRIES } from '../../utils/countries';
import type { Business, User, Payment, PaymentKind, RapnetSecrets, BillingCurrency } from '../../types';

function IconDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="3" r="1.3" fill="currentColor" />
      <circle cx="7" cy="7" r="1.3" fill="currentColor" />
      <circle cx="7" cy="11" r="1.3" fill="currentColor" />
    </svg>
  );
}

// Row action icons (same glyphs as Entity's RowActionsMenu).
function IconEdit() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /></svg>;
}
function IconTrash() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconBan() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.4" /><path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>;
}
function IconCircleCheck() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.4" /><path d="M4.5 7.2l1.7 1.7 3.3-3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}
function IconKeyReset() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="4.5" cy="9.5" r="2.5" stroke="currentColor" strokeWidth="1.4" /><path d="M6.3 7.7L11.5 2.5M9.5 4.5l1.5 1.5M10.8 3.2l1.2 1.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>;
}
function IconOpen() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M1.5 7S3.7 3 7 3s5.5 4 5.5 4-2.2 4-5.5 4S1.5 7 1.5 7z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><circle cx="7" cy="7" r="1.6" stroke="currentColor" strokeWidth="1.3" /></svg>;
}

interface RowAction { label: string; icon: ReactNode; onClick: () => void; danger?: boolean; title?: string }

/** One ⋮ button per table row; the actions open in a small menu with icons.
 *  Danger actions (Delete) go last, in red. */
function RowMenu({ actions }: { actions: RowAction[] }) {
  return (
    <div onClick={e => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Dropdown
        align="right"
        trigger={() => (
          <button type="button" className="icon-btn-round" title="Actions" aria-label="Actions">
            <IconDots />
          </button>
        )}
      >
        {actions.map(a => (
          <button key={a.label} type="button" title={a.title}
            className={`dropdown-item${a.danger ? ' danger' : ''}`} onClick={a.onClick}>
            {a.icon} {a.label}
          </button>
        ))}
      </Dropdown>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AdminBusiness extends Omit<Business, 'createdAt'> {
  createdAt: string;
}

// ── Dates: the Admin Portal always shows Day Month Year ("17 Oct 2026"). ──
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-10-17" → "17 Oct 2026" (local calendar date, no time zone shift). */
function fmtDMY(d?: string | null): string {
  if (!d) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (!m) return d;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}
/** Any "YYYY-MM-DD" inside a text (e.g. a payment's "Paid For" period) shown as Day Month Year. */
function dmyIn(text?: string | null): string {
  if (!text) return '—';
  return text.replace(/\d{4}-\d{2}-\d{2}/g, d => fmtDMY(d));
}

function fmtPeriod(start?: string, end?: string): string {
  if (!start && !end) return '—';
  if (start && end) return `${fmtDMY(start)} – ${fmtDMY(end)}`;
  if (start) return `From ${fmtDMY(start)}`;
  return `Until ${fmtDMY(end!)}`;
}

interface AdminUser extends Omit<User, 'createdAt'> {
  createdAt: string;
  businessName?: string;
}

interface AdminSession {
  userId: string;
  userName?: string;
  businessId: string;
  businessName?: string;
  deviceId: string;
  deviceInfo: string;
  loginAt: string;
  lastActiveAt: string;
  isActive: boolean;
}

interface AdminPayment extends Omit<Payment, 'id'> {
  id: string;
}

type ModalType = 'add-business' | 'edit-business' | 'business-history' | 'add-user' | 'edit-user' | 'add-payment' | 'edit-payment' | null;

const TAB_META: Record<AdminTab, { title: string; subtitle: string }> = {
  dashboard:  { title: 'Dashboard',  subtitle: 'Overview of every company, subscription, and payment across DiaWolt' },
  businesses: { title: 'Companies', subtitle: 'Manage every tenant company, subscription, and access' },
  users:      { title: 'Users',      subtitle: 'Manage every user across every business' },
  sessions:   { title: 'Sessions',   subtitle: 'Live and recent login sessions' },
  payments:   { title: 'Payments',   subtitle: 'Every logged payment across every business' },
};

// ── Dashboard period (By Country) ───────────────────────────────────────────
type DashPeriod = '1M' | '6M' | '1Y' | '2Y' | 'custom';
const DASH_PERIODS: { key: DashPeriod; label: string; months: number }[] = [
  { key: '1M', label: '1M', months: 1 }, { key: '6M', label: '6M', months: 6 },
  { key: '1Y', label: '1Y', months: 12 }, { key: '2Y', label: '2Y', months: 24 },
  { key: 'custom', label: 'Custom', months: 0 },
];
/** "2026-10-26" moved by n months, as a local date string. */
function shiftMonths(d: string, n: number): string {
  const [y, m, dd] = d.split('-').map(Number);
  const x = new Date(y, m - 1 + n, dd);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

// ── Subscription progress ───────────────────────────────────────────────────
// Local calendar days only (never toISOString, see renewal.ts).
const DAY_MS = 86_400_000;
function localDay(d: string) { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); }
function todayLocal() { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }

interface SubProgress { pct: number; usedDays: number; totalDays: number; daysLeft: number }
/** How far through its period a subscription is, and days to renewal. */
function subProgress(start?: string, end?: string): SubProgress | null {
  if (!end) return null;
  const endD  = localDay(end);
  const today = todayLocal();
  const daysLeft = Math.round((endD.getTime() - today.getTime()) / DAY_MS);
  // No start date: treat it as a one year period ending on the renewal date.
  const startD = start ? localDay(start) : new Date(endD.getFullYear() - 1, endD.getMonth(), endD.getDate());
  const totalDays = Math.max(1, Math.round((endD.getTime() - startD.getTime()) / DAY_MS));
  const usedDays  = Math.min(totalDays, Math.max(0, Math.round((today.getTime() - startD.getTime()) / DAY_MS)));
  return { pct: Math.round((usedDays / totalDays) * 100), usedDays, totalDays, daysLeft };
}

/** The Subscription column filter's groups. */
const RENEWAL_ORDER = ['Overdue', 'Due in 30 days', 'On track', 'No renewal date'];
function renewalBucket(start?: string, end?: string): string {
  const p = subProgress(start, end);
  if (!p) return 'No renewal date';
  return p.daysLeft < 0 ? 'Overdue' : p.daysLeft <= 30 ? 'Due in 30 days' : 'On track';
}
/** "2026-10-17" → "Oct 2026" (Paid Date filter) and its sort key "2026-10". */
function monthLabel(d?: string) {
  const m = d ? /^(\d{4})-(\d{2})/.exec(d) : null;
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : '';
}
function monthKey(label: string) {
  const [mon, y] = label.split(' ');
  const i = MONTHS.indexOf(mon);
  return i < 0 ? '' : `${y}-${String(i + 1).padStart(2, '0')}`;
}

/** Bar colour from days to renewal: red overdue, amber within 30 days, green otherwise. */
function renewalTone(daysLeft: number) {
  return daysLeft < 0 ? 'var(--danger)' : daysLeft <= 30 ? 'var(--warning)' : 'var(--success)';
}

function daysLeftText(daysLeft: number) {
  if (daysLeft < 0)  return `${-daysLeft} day${daysLeft === -1 ? '' : 's'} overdue`;
  if (daysLeft === 0) return 'Renews today';
  return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
}

/** "2 of 3" while within the included seats, "3 + 2 extra" beyond them. */
function seatsText(active: number, included: number) {
  const extra = Math.max(0, active - included);
  return extra > 0 ? `${included} + ${extra} extra` : `${active} of ${included}`;
}

/** Today as a local "YYYY-MM-DD" (never toISOString, see renewal.ts). */
function todayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** Small tag for a payment's type; older payments have none. */
function KindTag({ kind }: { kind?: PaymentKind }) {
  const tone = kind === 'first-year' ? 'var(--primary)' : kind === 'renewal' ? 'var(--success)'
    : kind === 'extra-users' ? 'var(--warning)' : 'var(--text-3)';
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
      color: tone, background: `color-mix(in srgb, ${tone} 10%, transparent)`,
      border: `1px solid color-mix(in srgb, ${tone} 30%, transparent)`,
    }}>
      {kind ? PAYMENT_KIND_LABEL[kind] : 'Payment'}
    </span>
  );
}

/** "€300 + 2 × €100" under a figure, only when extra users are billed (in the company's currency). */
function breakdownText(bill: Pick<Billing, 'feeAmt' | 'extraUsers' | 'extraUserRate' | 'currency'>) {
  return `${fmtBill(bill.feeAmt, bill.currency)} + ${bill.extraUsers} × ${fmtBill(bill.extraUserRate, bill.currency)}`;
}

function ProgressBar({ pct, color, height = 6 }: { pct: number; color: string; height?: number }) {
  return (
    <div style={{ height, borderRadius: 999, background: 'var(--surface-2)', border: '1px solid var(--border)', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.3s' }} />
    </div>
  );
}

/** A time as "17 Oct 2026, 16:10" (Day Month Year, then the time). */
function fmtDate(ts: unknown): string {
  if (!ts) return '—';
  let d: Date | null = null;
  if (ts instanceof Date) d = ts;
  else if (typeof ts === 'object' && ts !== null && 'toDate' in ts) d = (ts as { toDate: () => Date }).toDate();
  else if (typeof ts === 'string' || typeof ts === 'number') { const x = new Date(ts); if (!isNaN(x.getTime())) d = x; }
  if (!d) return String(ts);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ── Exchange rates for the "≈ €" figures ──────────────────────────────────
// Today's rates are fetched for every currency (fetchLiveRates). Here the
// admin can type a rate of their own for any currency in use; an empty box
// goes back to today's rate. Saved as adminConfig/billing.rateOverrides.
// Each payment keeps the rate of the day it was logged.
function RatesModal({ currencies, live, overrides, onClose, onSaved }: {
  currencies: string[]; live: { date: string; rates: RatesToEur } | null; overrides: RatesToEur;
  onClose: () => void; onSaved: (r: RatesToEur) => void;
}) {
  // Shown as "1 € = 92.12 ₹" (units per €), the way rates are usually quoted.
  const perEur = (v?: number) => (v && v > 0 ? String(Number((1 / v).toPrecision(6))) : '');
  const [form, setForm] = useState<Record<string, string>>(
    () => Object.fromEntries(currencies.map(c => [c, perEur(overrides[c])])));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    const next: RatesToEur = {};
    for (const c of currencies) {
      const v = form[c]?.trim();
      if (!v) continue;
      if (!(Number(v) > 0)) { setErr(`The ${c} rate must be above 0.`); return; }
      next[c] = 1 / Number(v);
    }
    setSaving(true); setErr('');
    try {
      await setDoc(doc(masterDb, 'adminConfig', 'billing'), { rateOverrides: next, updatedAt: serverTimestamp() }, { merge: true });
      onSaved(next); onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save the rates.'); }
    setSaving(false);
  }
  return (
    <Modal open onClose={onClose} title="Exchange Rates"
      subtitle={live ? `Today's rates, ${fmtDMY(live.date)}. Type one to use your own.` : 'Today\'s rates could not be loaded. Type the ones you need.'}
      size="sm"
      footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>Save Rates</Button>
      </div>}>
      {err && <div className="error-msg" style={{ marginBottom: 10 }}>{err}</div>}
      {currencies.length === 0 && <p className="admin-form-hint">Every company is billed in €.</p>}
      {currencies.map(c => (
        <div key={c} className="form-group">
          <label>1 € = … {c}</label>
          <input type="number" step="any" min="0" className="no-spinner" value={form[c] ?? ''}
            placeholder={perEur(live?.rates[c] ?? DEFAULT_RATES_TO_EUR[c]) || 'Rate'}
            onChange={e => setForm(f => ({ ...f, [c]: e.target.value }))} />
        </div>
      ))}
      <p className="admin-form-hint">Leave a box empty to use today's rate. Payments keep the rate of the day they were logged.</p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function AdminPanel() {
  const [tab, setTab] = useState<AdminTab>('dashboard');

  const [businesses, setBusinesses] = useState<AdminBusiness[]>([]);
  const [users, setUsers]           = useState<AdminUser[]>([]);
  const [sessions, setSessions]     = useState<AdminSession[]>([]);
  const [payments, setPayments]     = useState<AdminPayment[]>([]);
  // € per unit of each currency: today's fetched rates, with any rate the
  // admin typed in Exchange Rates (adminConfig/billing.rateOverrides) on top.
  const [liveRates, setLiveRates] = useState<{ date: string; rates: RatesToEur } | null>(null);
  const [rateOverrides, setRateOverrides] = useState<RatesToEur>({});
  const rates = useMemo<RatesToEur>(
    () => ({ ...DEFAULT_RATES_TO_EUR, ...liveRates?.rates, ...rateOverrides, EUR: 1 }),
    [liveRates, rateOverrides]);
  const [ratesOpen, setRatesOpen] = useState(false);
  // Dashboard › By Country period: Collected looks back over it, Coming Up ahead.
  const [dashPeriod, setDashPeriod] = useState<DashPeriod>('1Y');
  const [dashFrom, setDashFrom] = useState('');
  const [dashTo, setDashTo]     = useState('');
  useEffect(() => {
    let alive = true;
    fetchLiveRates(todayStr()).then(r => { if (alive && r) setLiveRates(r); });
    return () => { alive = false; };
  }, []);

  const [loading, setLoading]     = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  // Reset two step sign in (lost / new phone), via the resetTwoStep function.
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetBusy,   setResetBusy]   = useState(false);
  const [resetError,  setResetError]  = useState('');
  const [pageNotice,  setPageNotice]  = useState<string | null>(null);

  const [bizSortDue,    setBizSortDue]    = useState(false); // Companies: nearest due date first

  const [modal, setModal]                       = useState<ModalType>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<AdminBusiness | null>(null);
  const [selectedUser, setSelectedUser]         = useState<AdminUser | null>(null);
  const [selectedPayment, setSelectedPayment]   = useState<AdminPayment | null>(null);
  const [paymentDefaultUserId, setPaymentDefaultUserId] = useState<string>('');
  // Set together with paymentDefaultUserId when a payment is logged via the
  // Renewal Alerts row's "Renew" action — tells AddPaymentModal to also
  // extend that business's subscriptionEnd by 1 year on save. Plain "+ Log
  // Payment" elsewhere (Payments tab, business-history modal) leaves this
  // null, so it stays just a payment record, same as before.
  const [renewalBusinessId, setRenewalBusinessId] = useState<string | null>(null);
  // Prefills UserModal's business dropdown when "+ Add User" is opened from
  // one business's own detail modal, instead of defaulting to businesses[0].
  const [addUserDefaultBusinessId, setAddUserDefaultBusinessId] = useState<string>('');

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------
  const loadBusinesses = useCallback(async () => {
    const snap = await getDocs(query(collection(masterDb, 'businesses'), orderBy('createdAt', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AdminBusiness));
  }, []);

  const loadUsers = useCallback(async (bizList: AdminBusiness[]) => {
    const snap = await getDocs(query(collection(masterDb, 'users'), orderBy('createdAt', 'desc')));
    const bizMap = Object.fromEntries(bizList.map(b => [b.id, b.businessName]));
    return snap.docs.map(d => ({
      id: d.id, ...d.data(),
      businessName: bizMap[(d.data() as User).businessId] ?? 'Unknown',
    } as AdminUser));
  }, []);

  const loadSessions = useCallback(async (bizList: AdminBusiness[], userList: AdminUser[]) => {
    const snap = await getDocs(collection(masterDb, 'sessions'));
    const bizMap  = Object.fromEntries(bizList.map(b => [b.id, b.businessName]));
    const userMap = Object.fromEntries(userList.map(u => [u.id, u.name]));
    return snap.docs.map(d => ({
      userId: d.id, ...d.data(),
      businessName: bizMap[(d.data() as AdminSession).businessId] ?? 'Unknown',
      userName: userMap[d.id] ?? 'Unknown',
    } as AdminSession));
  }, []);

  const loadPayments = useCallback(async () => {
    const snap = await getDocs(query(collection(masterDb, 'payments'), orderBy('paidDate', 'desc')));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AdminPayment));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const bizList  = await loadBusinesses();
      const userList = await loadUsers(bizList);
      const sessionList = await loadSessions(bizList, userList);
      const paymentList = await loadPayments();
      setBusinesses(bizList);
      setUsers(userList);
      setSessions(sessionList);
      setPayments(paymentList);
      try {
        const cfg = await getDoc(doc(masterDb, 'adminConfig', 'billing'));
        setRateOverrides((cfg.data()?.rateOverrides ?? {}) as RatesToEur);
      } catch { /* rules not deployed yet: keep the fallback rates */ }
    } catch (err) {
      console.error(err);
      setPageError('Failed to load data. Check Firestore rules and your admin setup.');
    } finally {
      setLoading(false);
    }
  }, [loadBusinesses, loadUsers, loadSessions, loadPayments]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // User picker options for the payment forms: "Name (Company)", so typing
  // either a person or a company narrows the list.
  const userOptions = users.map(u => ({ id: u.id, companyName: `${u.name} (${u.businessName ?? ''})` }));

  // What a company owes for its NEXT payment: First Year until one is
  // logged, Renewal after that. Counts active users only.
  function billFor(b: Pick<AdminBusiness, 'id' | 'firstYearAmount' | 'baseAmount' | 'extraUserRate' | 'includedUsers'>,
                   stage = stageFor(b.id, payments)): Billing {
    return billingFor(b, users.filter(u => u.businessId === b.id && u.isActive).length, stage);
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  async function toggleBusiness(id: string, current: boolean) {
    await updateDoc(doc(masterDb, 'businesses', id), { isActive: !current });
    setBusinesses(prev => prev.map(b => b.id === id ? { ...b, isActive: !current } : b));
    // Keep the business-history modal's own copy in sync too, so toggling
    // Disable/Enable from inside it updates live instead of going stale.
    setSelectedBusiness(prev => prev && prev.id === id ? { ...prev, isActive: !current } : prev);
  }

  // "Disable Access" from a Renewal Alert row — same isActive kill switch as
  // the Businesses tab's toggle, just with a confirmation since it's reached
  // from a context where the business isn't already right in front of the
  // admin in the main table.
  async function disableBusinessAccess(b: AdminBusiness) {
    if (!window.confirm(`Disable access for "${b.businessName}"?\n\nThis immediately signs out and blocks login for every user at this business.`)) return;
    await toggleBusiness(b.id, true);
  }

  async function toggleUser(id: string, current: boolean) {
    await updateDoc(doc(masterDb, 'users', id), { isActive: !current });
    setUsers(prev => prev.map(u => u.id === id ? { ...u, isActive: !current } : u));
  }

  async function deleteBusiness(id: string, name: string) {
    if (!window.confirm(`Delete "${name}"?\n\nThis removes the business from your database. Users linked to it will no longer be able to log in. This cannot be undone.`)) return;
    await deleteDoc(doc(masterDb, 'businesses', id));
    setBusinesses(prev => prev.filter(b => b.id !== id));
  }

  async function deleteUser(id: string, name: string) {
    if (!window.confirm(`Delete "${name}"?\n\nThis removes their Firestore record — they will no longer be able to access the app.\n\nRemember to also delete their account from Firebase Console → Authentication.`)) return;
    await deleteDoc(doc(masterDb, 'users', id));
    // Also clear their session
    try { await deleteDoc(doc(masterDb, 'sessions', id)); } catch { /* ignore */ }
    setUsers(prev => prev.filter(u => u.id !== id));
  }

  async function confirmResetTwoStep() {
    if (!resetTarget) return;
    setResetBusy(true); setResetError('');
    try {
      const removed = await resetTwoStep(resetTarget.id);
      setPageNotice(removed > 0
        ? `Two step sign in was reset for ${resetTarget.name}. They can sign in with their password and set it up again.`
        : `${resetTarget.name} did not have two step sign in on. Nothing to reset.`);
      setResetTarget(null);
    } catch (e) {
      setResetError(functionErrorText(e));
    }
    setResetBusy(false);
  }

  async function deletePayment(id: string) {
    if (!window.confirm('Delete this payment record? This cannot be undone.')) return;
    await deleteDoc(doc(masterDb, 'payments', id));
    setPayments(prev => prev.filter(p => p.id !== id));
  }

  async function forceLogout(userId: string) {
    await updateDoc(doc(masterDb, 'sessions', userId), { isActive: false });
    setSessions(prev => prev.map(s => s.userId === userId ? { ...s, isActive: false } : s));
  }

  function openEditBusiness(b: AdminBusiness) {
    setSelectedBusiness(b);
    setModal('edit-business');
  }

  // Opens the payment modal in "renewal" mode for one business — prefills
  // its owner (or first user) as the payer and flags renewalBusinessId so
  // AddPaymentModal also extends subscriptionEnd by 1 year on save. Reused
  // by the Renewal Alerts row and the business-history modal's own Renew
  // button, so a business's subscription can be renewed from wherever the
  // admin happens to be looking at it.
  function openRenewFor(b: AdminBusiness) {
    const owner = users.find(u => u.businessId === b.id && u.role === 'owner')
               ?? users.find(u => u.businessId === b.id);
    setPaymentDefaultUserId(owner?.id ?? '');
    setRenewalBusinessId(b.id);
    setModal('add-payment');
  }

  function openAddUserFor(businessId: string) {
    setAddUserDefaultBusinessId(businessId);
    setModal('add-user');
  }

  function openEditUser(u: AdminUser) {
    setSelectedUser(u);
    setModal('edit-user');
  }

  // ---------------------------------------------------------------------------
  // Add / Edit Company Modal (writes to the 'businesses' collection — see
  // the multi-tenant architecture note above; only the on-screen label is
  // "Company", the schema/collection/type names stay Business* throughout)
  // ---------------------------------------------------------------------------
  function BusinessModal({ editing }: { editing: AdminBusiness | null }) {
    const isEdit = editing !== null;
    const [form, setForm] = useState({
      businessName:      editing?.businessName      ?? '',
      ownerName:         editing?.ownerName         ?? '',
      ownerEmail:        editing?.ownerEmail        ?? '',
      ownerPhone:        editing?.ownerPhone        ?? '',
      city:              editing?.city              ?? '',
      country:           editing?.country           ?? '',
      notes:             editing?.notes             ?? '',
      // '' = never set here (an older company keeps its own choice).
      tradingSystem:     (editing ? editing.tradingSystem ?? '' : 'belgian') as '' | 'belgian' | 'indian',
      apiKey:            editing?.firebaseConfig?.apiKey            ?? '',
      authDomain:        editing?.firebaseConfig?.authDomain        ?? '',
      projectId:         editing?.firebaseConfig?.projectId        ?? '',
      storageBucket:     editing?.firebaseConfig?.storageBucket    ?? '',
      messagingSenderId: editing?.firebaseConfig?.messagingSenderId ?? '',
      appId:             editing?.firebaseConfig?.appId            ?? '',
      // Subscription
      // A new company starts today and renews a year later.
      subscriptionStart: editing ? (editing.subscriptionStart ?? '') : todayStr(),
      subscriptionEnd:   editing ? (editing.subscriptionEnd   ?? '') : addOneYear(todayStr()),
      // Standard plan pre-filled: €999 first year, €300 renewal, 3 users
      // included, €100 per extra user. An existing company keeps its own.
      billingCurrency:   billingCurrencyOf(editing) as string,
      firstYearAmount:   String(editing ? firstYearAmountOf(editing) : PLAN_EUR.firstYear),
      baseAmount:        String(editing ? renewalAmountOf(editing)   : PLAN_EUR.renewal),
      extraUserRate:     String(editing ? extraUserRateOf(editing)   : PLAN_EUR.extraUser),
      includedUsers:     String(editing ? includedUsersOf(editing) : DEFAULT_INCLUDED_USERS),
      // RapNet keys: kept in businessSecrets/{id}, loaded below on edit.
      rapnetClientId:     '',
      rapnetClientSecret: '',
    });
    const [err, setErr]       = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    // New company only: also record the First Year payment in the same save.
    const [paidNow, setPaidNow] = useState(false);
    const [secretsLoaded, setSecretsLoaded] = useState(!isEdit);
    // The keys as last saved, so Save only writes them when they changed.
    const [savedKeys, setSavedKeys] = useState({ id: '', secret: '' });

    useEffect(() => {
      if (!isEdit) return;
      let alive = true;
      getDoc(doc(masterDb, 'businessSecrets', editing!.id))
        .then(snap => {
          if (!alive) return;
          const d = (snap.data() ?? {}) as RapnetSecrets;
          setSavedKeys({ id: d.rapnetClientId ?? '', secret: d.rapnetClientSecret ?? '' });
          setForm(prev => ({ ...prev, rapnetClientId: d.rapnetClientId ?? '', rapnetClientSecret: d.rapnetClientSecret ?? '' }));
        })
        .catch(() => { /* rules not deployed yet: fields just start empty */ })
        .finally(() => { if (alive) setSecretsLoaded(true); });
      return () => { alive = false; };
    }, [isEdit, editing]);

    function set(field: string, value: string) {
      setForm(prev => ({ ...prev, [field]: value }));
    }
    // Changing the billing currency swaps in the € plan converted at
    // today's rate, unless a price was typed by hand (then it stays).
    function setCurrency(next: BillingCurrency) {
      setForm(prev => {
        if (prev.billingCurrency === next) return prev;
        const old = planFor(prev.billingCurrency, rates);
        const nw  = planFor(next, rates);
        const keep = (v: string, std: number) => v !== '' && Number(v) !== std;
        return {
          ...prev, billingCurrency: next,
          firstYearAmount: keep(prev.firstYearAmount, old.firstYear) ? prev.firstYearAmount : String(nw.firstYear),
          baseAmount:      keep(prev.baseAmount, old.renewal)        ? prev.baseAmount      : String(nw.renewal),
          extraUserRate:   keep(prev.extraUserRate, old.extraUser)   ? prev.extraUserRate   : String(nw.extraUser),
        };
      });
    }
    const formCur = form.billingCurrency as BillingCurrency;
    const curSym = curSymbol(formCur);
    // "≈ €999" under a price in another currency.
    const approx = (v: string) => eurApprox(Number(v) || 0, formCur, rates);
    const countryCur = form.country.trim() ? currencyOfCountry(form.country) : null;

    async function handleSubmit(e: FormEvent) {
      e.preventDefault();
      setErr(null);
      // Any length is allowed (a demo can run a few weeks); the end just has
      // to come after the start.
      if (form.subscriptionStart && form.subscriptionEnd && form.subscriptionEnd <= form.subscriptionStart) {
        setErr('Renewal Date must be after Subscription Start.');
        return;
      }
      setSaving(true);
      const data = {
        businessName: form.businessName,
        ownerName:    form.ownerName,
        ownerEmail:   form.ownerEmail,
        ownerPhone:   form.ownerPhone,
        city:         form.city,
        country:      form.country,
        notes:        form.notes,
        firebaseConfig: {
          apiKey:            form.apiKey,
          authDomain:        form.authDomain,
          projectId:         form.projectId,
          storageBucket:     form.storageBucket,
          messagingSenderId: form.messagingSenderId,
          appId:             form.appId,
        },
        subscriptionStart: form.subscriptionStart || null,
        subscriptionEnd:   form.subscriptionEnd   || null,
        billingCurrency:   formCur,
        firstYearAmount:   form.firstYearAmount !== '' ? Number(form.firstYearAmount) : planFor(formCur, rates).firstYear,
        baseAmount:        form.baseAmount      !== '' ? Number(form.baseAmount)      : planFor(formCur, rates).renewal,
        extraUserRate:     form.extraUserRate   !== '' ? Number(form.extraUserRate)   : planFor(formCur, rates).extraUser,
        includedUsers:     Number(form.includedUsers) > 0 ? Math.floor(Number(form.includedUsers)) : DEFAULT_INCLUDED_USERS,
        rapnetConfigured:  !!(form.rapnetClientId.trim() && form.rapnetClientSecret.trim()),
        ...(form.tradingSystem ? { tradingSystem: form.tradingSystem } : {}),
      };
      try {
        let businessId: string;
        if (isEdit) {
          businessId = editing!.id;
          await updateDoc(doc(masterDb, 'businesses', businessId), data);
        } else {
          // Company + (optionally) its First Year payment in one batch, so
          // there's never a company whose "paid now" payment went missing.
          const ref = doc(collection(masterDb, 'businesses'));
          businessId = ref.id;
          const batch = writeBatch(masterDb);
          batch.set(ref, {
            ...data,
            isActive:  true,
            createdAt: serverTimestamp(),
            createdBy: 'admin',
          });
          if (paidNow) {
            // No logins exist yet, so the payment is in the owner's name
            // with no user id; extra users are 0 at this point.
            const bill = billingFor(data, 0, 'first-year');
            batch.set(doc(collection(masterDb, 'payments')), {
              businessId,
              userId:        '',
              userName:      form.ownerName,
              amount:        bill.totalDue,
              currency:      formCur,
              rateToEur:     toEur(1, formCur, rates),
              paidDate:      form.subscriptionStart || todayStr(),
              paidFor:       `${fmtDMY(form.subscriptionStart || todayStr())} – ${fmtDMY(form.subscriptionEnd || addOneYear(todayStr()))}`,
              collectedBy:   'admin',
              paymentMethod: 'cash',
              notes:         '',
              kind:          'first-year',
              periodStart:   form.subscriptionStart || todayStr(),
              periodEnd:     form.subscriptionEnd || addOneYear(todayStr()),
              ...paymentBreakdown(bill),
              createdAt:     serverTimestamp(),
            });
          }
          await batch.commit();
        }
        // The company is saved: always close the form now.
        setModal(null);
        setSelectedBusiness(null);
        // Keys go to the admin only secrets doc, never the business doc, and
        // only when they changed (skipped until the saved keys have loaded,
        // so a slow read can't blank them out). A failure here is shown on
        // the page instead of keeping the form open.
        const keyId = form.rapnetClientId.trim(), keySecret = form.rapnetClientSecret.trim();
        let keysError: string | null = null;
        if (secretsLoaded && (keyId !== savedKeys.id || keySecret !== savedKeys.secret)) {
          try {
            await setDoc(doc(masterDb, 'businessSecrets', businessId), {
              rapnetClientId:     keyId     || null,
              rapnetClientSecret: keySecret || null,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          } catch {
            keysError = 'Company saved, but the RapNet keys could not be saved. Publish the latest Firestore rules and try again.';
          }
        }
        await loadAll();
        if (keysError) setPageError(keysError);
      } catch (e: unknown) {
        setErr(String(e));
        setSaving(false);
      }
    }

    return (
      <Modal
        open
        onClose={() => { setModal(null); setSelectedBusiness(null); }}
        title={isEdit ? 'Edit Company' : 'Add New Company'}
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setModal(null); setSelectedBusiness(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form="admin-business-form" variant="primary" loading={saving}>
              {isEdit ? 'Save Changes' : 'Add Company'}
            </Button>
          </>
        }
      >
          {err && <div className="error-msg">{err}</div>}
          <form id="admin-business-form" onSubmit={handleSubmit}>
            <div className="admin-form-grid">
              <div className="form-group">
                <label>Company Name *</label>
                <input required value={form.businessName} onChange={e => set('businessName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Owner Name *</label>
                <input required value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Owner Email *</label>
                <input required type="email" value={form.ownerEmail} onChange={e => set('ownerEmail', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Owner Phone</label>
                <input value={form.ownerPhone} onChange={e => set('ownerPhone', e.target.value)} />
              </div>
              <div className="form-group">
                <label>City</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Country</label>
                <OptionCombobox value={form.country} options={COUNTRIES} openOnArrow placeholder="Type or pick" onChange={v => {
                  set('country', v);
                  // Billed in the country's own currency (India ₹, UAE AED…).
                  if (v.trim()) setCurrency(currencyOfCountry(v));
                  // A new Indian company trades the Indian way (still editable).
                  if (!isEdit && v.trim()) set('tradingSystem', v.trim().toLowerCase() === 'india' ? 'indian' : 'belgian');
                }} />
              </div>
              <div className="form-group">
                <label>Trading System</label>
                <select value={form.tradingSystem} onChange={e => set('tradingSystem', e.target.value)}>
                  {form.tradingSystem === '' && <option value="">Company's own choice</option>}
                  <option value="belgian">Belgian (no tax per invoice)</option>
                  <option value="indian">Indian (Less 1 / Less 2, GST)</option>
                </select>
              </div>
            </div>

            <p className="admin-form-section">Subscription &amp; Billing</p>
            <div className="admin-form-grid admin-form-grid-7">
              <div className="form-group">
                <label>Billing Currency</label>
                <select value={form.billingCurrency} onChange={e => setCurrency(e.target.value as BillingCurrency)}>
                  {[...new Set([...BILLING_CURRENCY_CODES, form.billingCurrency])].sort()
                    .map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
                </select>
                {countryCur && countryCur !== formCur && (
                  <button type="button" className="line-item-link-btn" style={{ fontSize: 11.5, marginTop: 4 }}
                    onClick={() => setCurrency(countryCur)}>
                    {form.country.trim()} uses {countryCur}. Switch
                  </button>
                )}
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input type="date" value={form.subscriptionStart} onChange={e => set('subscriptionStart', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Renewal Date</label>
                <input type="date" value={form.subscriptionEnd} onChange={e => set('subscriptionEnd', e.target.value)} />
              </div>
              <div className="form-group">
                <label>First Year ({curSym})</label>
                <input type="number" step="0.01" min="0" className="no-spinner" value={form.firstYearAmount} onChange={e => set('firstYearAmount', e.target.value)} />
                {approx(form.firstYearAmount) && <span className="admin-form-hint" style={{ display: 'block', marginTop: 3 }}>{approx(form.firstYearAmount)}</span>}
              </div>
              <div className="form-group">
                <label>Renewal / Year ({curSym})</label>
                <input type="number" step="0.01" min="0" className="no-spinner" value={form.baseAmount} onChange={e => set('baseAmount', e.target.value)} />
                {approx(form.baseAmount) && <span className="admin-form-hint" style={{ display: 'block', marginTop: 3 }}>{approx(form.baseAmount)}</span>}
              </div>
              <div className="form-group">
                <label>Users Included</label>
                <input type="number" step="1" min="1" className="no-spinner" value={form.includedUsers} onChange={e => set('includedUsers', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Extra User / Year ({curSym})</label>
                <input type="number" step="0.01" min="0" className="no-spinner" value={form.extraUserRate} onChange={e => set('extraUserRate', e.target.value)} />
                {approx(form.extraUserRate) && <span className="admin-form-hint" style={{ display: 'block', marginTop: 3 }}>{approx(form.extraUserRate)}</span>}
              </div>
            </div>
            <p className="admin-form-hint">
              Prices are in the billing currency, which follows the Country. Picking one fills in the € plan at today's rate. First year includes setup. Both prices cover the included users (the owner counts as one). Extra users are added at each renewal.
            </p>
            {!isEdit && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-1)', marginTop: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={paidNow} onChange={e => setPaidNow(e.target.checked)} style={{ accentColor: 'var(--primary)' }} />
                First year paid now
                <span style={{ color: 'var(--text-3)' }}>
                  (records a {fmtBill(Number(form.firstYearAmount) || 0, formCur)} First Year payment)
                </span>
              </label>
            )}

            <p className="admin-form-section">Client Firebase Config</p>
            <div className="admin-form-grid">
              <div className="form-group">
                <label>API Key *</label>
                <input required value={form.apiKey} onChange={e => set('apiKey', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Auth Domain *</label>
                <input required value={form.authDomain} onChange={e => set('authDomain', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Project ID *</label>
                <input required value={form.projectId} onChange={e => set('projectId', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Storage Bucket</label>
                <input value={form.storageBucket} onChange={e => set('storageBucket', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Messaging Sender ID</label>
                <input value={form.messagingSenderId} onChange={e => set('messagingSenderId', e.target.value)} />
              </div>
              <div className="form-group">
                <label>App ID *</label>
                <input required value={form.appId} onChange={e => set('appId', e.target.value)} />
              </div>
            </div>

            <p className="admin-form-section">RapNet</p>
            <div className="admin-form-grid">
              <div className="form-group">
                <label>Client ID</label>
                <input value={form.rapnetClientId} onChange={e => set('rapnetClientId', e.target.value)}
                  disabled={!secretsLoaded} autoComplete="off" spellCheck={false}
                  placeholder={secretsLoaded ? '' : 'Loading…'} />
              </div>
              <div className="form-group">
                <label>Client Secret</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input type={showSecret ? 'text' : 'password'} value={form.rapnetClientSecret}
                    onChange={e => set('rapnetClientSecret', e.target.value)}
                    disabled={!secretsLoaded} autoComplete="new-password" spellCheck={false}
                    style={{ flex: 1, minWidth: 0 }} />
                  <Button type="button" variant="secondary" onClick={() => setShowSecret(v => !v)}
                    style={{ flexShrink: 0 }}>
                    {showSecret ? 'Hide' : 'Show'}
                  </Button>
                </div>
              </div>
            </div>
            <p className="admin-form-hint">Optional. Used to fetch certificate details. Only admins can see these keys.</p>

            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
          </form>
      </Modal>
    );
  }

  // ---------------------------------------------------------------------------
  // Add / Edit User Modal
  // ---------------------------------------------------------------------------
  function UserModal({ editing }: { editing: AdminUser | null }) {
    const isEdit = editing !== null;
    const [form, setForm] = useState({
      name:       editing?.name       ?? '',
      email:      editing?.email      ?? '',
      password:   '',
      phone:      editing?.phone      ?? '',
      businessId: editing?.businessId ?? (addUserDefaultBusinessId || businesses[0]?.id || ''),
      // New login: Owner if the company has none yet, else Staff.
      role:       (editing?.role ?? (users.some(u => u.businessId === (addUserDefaultBusinessId || businesses[0]?.id) && u.role === 'owner')
                    ? 'staff' : 'owner')) as 'owner' | 'staff',
      paidAmount: editing?.paidAmount  != null ? String(editing.paidAmount) : '',
      paidDate:   editing?.paidDate   ?? '',
      expiryDate: editing?.expiryDate ?? '',
    });
    const [err, setErr]             = useState<string | null>(null);
    const [saving, setSaving]       = useState(false);
    const [createdUid, setCreatedUid] = useState<string | null>(null);

    function set(field: string, value: string) {
      setForm(prev => ({ ...prev, [field]: value }));
    }

    // Seat check: the company's fee covers its first N active users (owner
    // included, see utils/billing.ts). Within that this login is free; past
    // it, it's an extra user billed at the company's rate at the next
    // renewal. Billing itself counts active users; isFree mirrors this for
    // the "free" badge.
    const seatBiz     = businesses.find(b => b.id === form.businessId);
    const seatIncl    = seatBiz ? includedUsersOf(seatBiz) : DEFAULT_INCLUDED_USERS;
    const seatOthers  = users.filter(u => u.businessId === form.businessId && u.isActive && u.id !== editing?.id).length;
    const seatNo      = seatOthers + 1;
    const seatFree    = seatNo <= seatIncl;
    const seatRate    = seatBiz ? extraUserRateOf(seatBiz) : PLAN_EUR.extraUser;
    const seatCur     = billingCurrencyOf(seatBiz);

    async function handleSubmit(e: FormEvent) {
      e.preventDefault();
      setSaving(true);
      setErr(null);
      try {
        const isFree = form.role === 'owner' || seatFree;
        if (isEdit) {
          await updateDoc(doc(masterDb, 'users', editing!.id), {
            name:       form.name,
            phone:      form.phone,
            businessId: form.businessId,
            role:       form.role,
            isFree,
          });
          setModal(null);
          setSelectedUser(null);
          await loadAll();
        } else {
          const uid = await createFirebaseAuthUser(form.email, form.password);
          setCreatedUid(uid);
          await setDoc(doc(masterDb, 'users', uid), {
            businessId: form.businessId,
            name:       form.name,
            email:      form.email,
            phone:      form.phone,
            role:       form.role,
            isActive:   true,
            isFree,
            createdAt:  serverTimestamp(),
            createdBy:  'admin',
            // A brand-new staff login starts with zero page access — the
            // owner grants pages from their own Settings > Team & Access.
            // (An 'owner' role bypasses page checks entirely, so this is
            // only meaningful for 'staff' — and must be an explicit []
            // rather than left unset, since canAccessPage treats a MISSING
            // allowedPages field as "predates this feature, full access"
            // to avoid breaking every already-existing staff login.)
            ...(form.role === 'staff' ? { allowedPages: [] as string[] } : {}),
          });
          await loadAll();
        }
      } catch (e: unknown) {
        const msg = (e as { message?: string }).message ?? String(e);
        setErr(msg.includes('email-already-in-use')
          ? 'A Firebase Auth account with this email already exists.'
          : msg);
        setSaving(false);
      }
    }

    if (createdUid) {
      return (
        <Modal
          open
          onClose={() => { setModal(null); setCreatedUid(null); }}
          title="User Created"
          size="sm"
          footer={
            <Button variant="primary" onClick={() => { setModal(null); setCreatedUid(null); }}>
              Done
            </Button>
          }
        >
            <div className="info-msg">Firebase Auth account and Firestore record created successfully.</div>
            <div className="detail-row">
              <span className="detail-label">Name</span>
              <span className="detail-value">{form.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span className="detail-value">{form.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">UID</span>
              <span className="detail-value" style={{ fontFamily: 'var(--font-num)', fontSize: 12 }}>{createdUid}</span>
            </div>
        </Modal>
      );
    }

    return (
      <Modal
        open
        onClose={() => { setModal(null); setSelectedUser(null); }}
        title={isEdit ? 'Edit User' : 'Add New User'}
        footer={
          <>
            <Button variant="secondary" onClick={() => { setModal(null); setSelectedUser(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form="admin-user-form" variant="primary" loading={saving}>
              {isEdit ? 'Save Changes' : 'Create User'}
            </Button>
          </>
        }
      >
          {err && <div className="error-msg">{err}</div>}
          <form id="admin-user-form" onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <div className="form-group">
                <label>Full Name *</label>
                <input required value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
              {!isEdit && (
                <>
                  <div className="form-group">
                    <label>Email *</label>
                    <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input required type="password" minLength={6} value={form.password} onChange={e => set('password', e.target.value)} />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>Company *</label>
                <select required value={form.businessId} onChange={e => set('businessId', e.target.value)}>
                  {businesses.map(b => (
                    <option key={b.id} value={b.id}>{b.businessName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select value={form.role} onChange={e => set('role', e.target.value)}>
                  <option value="owner">Owner</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
            </div>

            {/* Free within the included users; extra beyond them. */}
            {form.businessId && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 'var(--r-sm)', fontSize: 12.5,
                background: seatFree ? 'var(--success-bg)' : 'color-mix(in srgb, var(--warning) 10%, transparent)',
                border: `1px solid color-mix(in srgb, ${seatFree ? 'var(--success)' : 'var(--warning)'} 30%, transparent)`,
                color: 'var(--text-1)',
              }}>
                <strong style={{ color: seatFree ? 'var(--success)' : 'var(--warning)', whiteSpace: 'nowrap' }}>
                  {seatFree ? 'Free' : `${fmtBill(seatRate, seatCur)} / year`}
                </strong>
                <span style={{ color: 'var(--text-2)' }}>
                  {seatFree
                    ? `User ${seatNo} of ${seatIncl} included in the plan.`
                    : `User ${seatNo}, the plan includes ${seatIncl}. Added to the next renewal as an extra user.`}
                </span>
              </div>
            )}
          </form>
      </Modal>
    );
  }

  // ---------------------------------------------------------------------------
  // Add Payment Modal
  // ---------------------------------------------------------------------------
  function AddPaymentModal() {
    // Renewal mode: only set when this modal was opened via openRenewFor()
    // (the Renewal Alerts row, or business-history's own Renew button) —
    // a plain "+ Log Payment" elsewhere leaves this null and the modal
    // behaves exactly as a payment-only record, unchanged.
    const renewingBusiness = renewalBusinessId ? businesses.find(b => b.id === renewalBusinessId) ?? null : null;
    const newEnd = renewingBusiness ? computeRenewalEnd(renewingBusiness.subscriptionEnd) : null;

    // Type + amount: Renew is always a Renewal; a plain Log Payment starts
    // on whatever the company owes next (First Year until one is logged).
    // Picking a type or user re-suggests the amount from the price list.
    const bizOfUser = (userId: string) => {
      const bid = users.find(u => u.id === userId)?.businessId;
      return bid ? businesses.find(b => b.id === bid) ?? null : null;
    };
    function suggestFor(kind: PaymentKind, userId: string) {
      const biz = renewingBusiness ?? bizOfUser(userId);
      if (!biz || (kind !== 'first-year' && kind !== 'renewal')) return null;
      return { amount: String(billFor(biz, kind).totalDue),
               paidFor: kind === 'first-year' && biz.subscriptionStart && biz.subscriptionEnd
                 ? `${fmtDMY(biz.subscriptionStart)} – ${fmtDMY(biz.subscriptionEnd)}` : null };
    }
    const initialUser = paymentDefaultUserId || users[0]?.id || '';
    const initialBiz  = renewingBusiness ?? bizOfUser(initialUser);
    const initialKind: PaymentKind = renewingBusiness ? 'renewal' : initialBiz ? stageFor(initialBiz.id, payments) : 'other';
    const initialSug  = suggestFor(initialKind, initialUser);
    const [form, setForm] = useState<{ kind: PaymentKind; userId: string; currency: string; amount: string; paidDate: string; paidFor: string; notes: string }>({
      kind:     initialKind,
      userId:   initialUser,
      // The company's billing currency, but any currency can be picked.
      currency: billingCurrencyOf(initialBiz),
      amount:   initialSug?.amount ?? '',
      paidDate: todayStr(),
      paidFor:  renewingBusiness ? `${renewingBusiness.subscriptionEnd ? fmtDMY(renewingBusiness.subscriptionEnd) : 'today'} – ${fmtDMY(newEnd)}` : (initialSug?.paidFor ?? ''),
      notes:    '',
    });
    function pick(kind: PaymentKind, userId: string) {
      const sug = suggestFor(kind, userId);
      const biz = renewingBusiness ?? bizOfUser(userId);
      setForm(prev => ({
        ...prev, kind, userId,
        ...(sug ? { amount: sug.amount, currency: billingCurrencyOf(biz) } : {}),
        ...(sug?.paidFor && !renewingBusiness ? { paidFor: sug.paidFor } : {}),
      }));
    }
    const formBiz  = renewingBusiness ?? bizOfUser(form.userId);
    const formBill = formBiz && (form.kind === 'first-year' || form.kind === 'renewal') ? billFor(formBiz, form.kind) : null;
    const [err, setErr]       = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    function set(field: string, value: string) {
      setForm(prev => ({ ...prev, [field]: value }));
    }
    // Another currency: the amount is converted at today's rate (from the
    // price list when there is one), then can be typed over.
    function setPayCurrency(next: string) {
      setForm(prev => {
        const fromCur = formBill ? formBill.currency : prev.currency;
        const fromAmt = formBill ? formBill.totalDue : Number(prev.amount);
        const perUnit = toEur(1, next, rates);
        const amount = fromAmt > 0 && perUnit > 0
          ? String(Math.round((toEur(fromAmt, fromCur, rates) / perUnit) * 100) / 100)
          : prev.amount;
        return { ...prev, currency: next, amount };
      });
    }

    function close() {
      setModal(null);
      setRenewalBusinessId(null);
    }

    async function handleSubmit(e: FormEvent) {
      e.preventDefault();
      setSaving(true);
      setErr(null);
      try {
        const selectedUser = users.find(u => u.id === form.userId);
        if (!selectedUser) { setErr('Pick the user who paid.'); setSaving(false); return; }
        const payBiz = businesses.find(b => b.id === selectedUser.businessId);
        // First Year / Renewal payments keep the price breakdown as it was
        // today, so a later price change never rewrites this record.
        const bill = payBiz && (form.kind === 'first-year' || form.kind === 'renewal') ? billFor(payBiz, form.kind) : null;
        const paymentData = {
          businessId:    selectedUser.businessId,
          userId:        form.userId,
          userName:      selectedUser.name,
          amount:        Number(form.amount),
          // In the currency it was paid in, with today's € rate kept.
          currency:      form.currency,
          rateToEur:     toEur(1, form.currency, rates),
          paidDate:      form.paidDate,
          paidFor:       form.paidFor,
          collectedBy:   'admin',
          paymentMethod: 'cash',
          notes:         form.notes,
          kind:          form.kind,
          // The fee breakdown is in the company's currency: only kept when paid in it.
          ...(bill && bill.currency === form.currency ? paymentBreakdown(bill) : {}),
          ...(form.kind === 'first-year' && payBiz?.subscriptionStart && payBiz.subscriptionEnd
            ? { periodStart: payBiz.subscriptionStart, periodEnd: payBiz.subscriptionEnd } : {}),
          createdAt:     serverTimestamp(),
        };
        if (renewalBusinessId) {
          // Renewal: write the payment AND extend subscriptionEnd, atomically —
          // reading the business doc fresh inside the transaction (not the
          // possibly-stale client copy) before computing the new date.
          const bizRef = doc(masterDb, 'businesses', renewalBusinessId);
          const payRef = doc(collection(masterDb, 'payments'));
          await runTransaction(masterDb, async tx => {
            const bizSnap = await tx.get(bizRef);
            if (!bizSnap.exists()) throw new Error('Company no longer exists.');
            const oldEnd   = (bizSnap.data() as AdminBusiness).subscriptionEnd;
            const freshEnd = computeRenewalEnd(oldEnd);
            // The renewed period: from the old renewal date (or today, if
            // it was already overdue) to the new one.
            const periodStart = oldEnd && oldEnd > todayStr() ? oldEnd : todayStr();
            tx.set(payRef, { ...paymentData, periodStart, periodEnd: freshEnd });
            tx.update(bizRef, { subscriptionEnd: freshEnd });
          });
        } else {
          await addDoc(collection(masterDb, 'payments'), paymentData);
        }
        close();
        await loadAll();
      } catch (e: unknown) {
        setErr(String(e));
        setSaving(false);
      }
    }

    return (
      <Modal
        open
        onClose={close}
        title={renewingBusiness ? `Renew — ${renewingBusiness.businessName}` : 'Log Payment'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
            <Button type="submit" form="admin-payment-form" variant="primary" loading={saving}>
              {renewingBusiness ? 'Renew' : 'Log Payment'}
            </Button>
          </>
        }
      >
          {err && <div className="error-msg">{err}</div>}
          {newEnd && (
            <div className="info-msg" style={{ marginBottom: 14 }}>
              Renews through <strong>{fmtDMY(newEnd)}</strong> when saved.
            </div>
          )}
          <form id="admin-payment-form" onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 12px' }}>
              <div className="form-group">
                <label>Type *</label>
                <select value={form.kind} disabled={!!renewingBusiness}
                  onChange={e => pick(e.target.value as PaymentKind, form.userId)}>
                  {(Object.keys(PAYMENT_KIND_LABEL) as PaymentKind[]).map(k => (
                    <option key={k} value={k}>{PAYMENT_KIND_LABEL[k]}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>User *</label>
                {/* Type to filter by user or company name. */}
                <EntityCombobox
                  entities={userOptions}
                  value={form.userId}
                  onChange={id => pick(form.kind, id)}
                  placeholder="Type a user or company"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
              <div className="form-group">
                <label>Currency *</label>
                <select value={form.currency} onChange={e => setPayCurrency(e.target.value)}>
                  {[...new Set([...BILLING_CURRENCY_CODES, form.currency])].sort()
                    .map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Amount ({curSymbol(form.currency)}) *</label>
                <input required type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} />
                {formBill && (
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    {STAGE_LABEL[formBill.stage]} {fmtBill(formBill.feeAmt, formBill.currency)}
                    {formBill.extraUsers > 0 && ` + ${formBill.extraUsers} extra × ${fmtBill(formBill.extraUserRate, formBill.currency)}`}
                    {' = '}{fmtBill(formBill.totalDue, formBill.currency)}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Paid Date *</label>
                <input required type="date" value={form.paidDate} onChange={e => set('paidDate', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Paid For (period) *</label>
              <input required placeholder="e.g. 17 Oct 2026 – 17 Oct 2027" value={form.paidFor} onChange={e => set('paidFor', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
          </form>
      </Modal>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit Payment Modal
  // ---------------------------------------------------------------------------
  function EditPaymentModal({ editing }: { editing: AdminPayment }) {
    const [form, setForm] = useState({
      kind:    (editing.kind ?? '') as PaymentKind | '',
      userId:  editing.userId,
      currency: paymentCurrencyOf(editing),
      amount:  String(editing.amount),
      paidDate: editing.paidDate,
      paidFor:  editing.paidFor,
      notes:    editing.notes ?? '',
    });
    const [err, setErr]       = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    function set(field: string, value: string) {
      setForm(prev => ({ ...prev, [field]: value }));
    }

    async function handleSubmit(e: FormEvent) {
      e.preventDefault();
      setSaving(true);
      setErr(null);
      try {
        // userId '' = a First Year payment saved with the company, before
        // any login existed: it stays in the owner's name.
        const u = form.userId ? users.find(u => u.id === form.userId) : null;
        if (form.userId && !u) throw new Error('User not found');
        await updateDoc(doc(masterDb, 'payments', editing.id), {
          userId:     form.userId,
          businessId: u ? u.businessId : editing.businessId,
          userName:   u ? u.name : editing.userName,
          kind:       form.kind || null,
          amount:     Number(form.amount),
          // A corrected currency takes today's € rate; otherwise the saved one stays.
          ...(form.currency !== paymentCurrencyOf(editing)
            ? { currency: form.currency, rateToEur: toEur(1, form.currency, rates) } : {}),
          paidDate:   form.paidDate,
          paidFor:    form.paidFor,
          notes:      form.notes || null,
        });
        setModal(null);
        setSelectedPayment(null);
        await loadAll();
      } catch (e: unknown) {
        setErr(String(e));
        setSaving(false);
      }
    }

    return (
      <Modal
        open
        onClose={() => { setModal(null); setSelectedPayment(null); }}
        title="Edit Payment"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setModal(null); setSelectedPayment(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form="admin-edit-payment-form" variant="primary" loading={saving}>
              Save Changes
            </Button>
          </>
        }
      >
          {err && <div className="error-msg">{err}</div>}
          <form id="admin-edit-payment-form" onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 12px' }}>
              <div className="form-group">
                <label>Type</label>
                <select value={form.kind} onChange={e => set('kind', e.target.value)}>
                  {!editing.kind && <option value="">Payment</option>}
                  {(Object.keys(PAYMENT_KIND_LABEL) as PaymentKind[]).map(k => (
                    <option key={k} value={k}>{PAYMENT_KIND_LABEL[k]}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>User</label>
                <EntityCombobox
                  entities={userOptions}
                  value={form.userId}
                  onChange={id => set('userId', id)}
                  placeholder={editing.userId ? 'Type a user or company' : `${editing.userName} (owner)`}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 12px' }}>
              <div className="form-group">
                <label>Currency *</label>
                <select value={form.currency} onChange={e => set('currency', e.target.value)}>
                  {[...new Set([...BILLING_CURRENCY_CODES, form.currency])].sort()
                    .map(c => <option key={c} value={c}>{currencyLabel(c)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Amount ({curSymbol(form.currency)}) *</label>
                <input required type="number" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Paid Date *</label>
                <input required type="date" value={form.paidDate} onChange={e => set('paidDate', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Paid For (period) *</label>
              <input required placeholder="e.g. 17 Oct 2026 – 17 Oct 2027" value={form.paidFor} onChange={e => set('paidFor', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
          </form>
      </Modal>
    );
  }

  // ---------------------------------------------------------------------------
  // Filtered lists (client-side search)
  // ---------------------------------------------------------------------------
  // No search boxes: the column filters do that job.
  const searchedBusinesses = businesses;

  // Excel-style column filters on each table (same as the app's list pages).
  const bizColumns = useMemo<FilterColumn<AdminBusiness>[]>(() => [
    { key: 'company', label: 'Company', get: b => b.businessName },
    { key: 'owner',   label: 'Owner',   get: b => b.ownerName },
    { key: 'country', label: 'Country', get: b => b.country },
    { key: 'currency', label: 'Currency', get: b => billingCurrencyOf(b) },
    { key: 'renewal', label: 'Subscription', get: b => renewalBucket(b.subscriptionStart, b.subscriptionEnd),
      sort: (x, y) => RENEWAL_ORDER.indexOf(x) - RENEWAL_ORDER.indexOf(y) },
    { key: 'seats',   label: 'Users',   get: b => (billFor(b).extraUsers > 0 ? 'Extra users' : 'Within plan') },
    { key: 'stage',   label: 'Amount Due', get: b => STAGE_LABEL[billFor(b).stage] },
    { key: 'status',  label: 'Status',  get: b => (b.isActive ? 'Active' : 'Inactive') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [users, payments]);
  const { rows: bizFiltered, filters: bizFilters } = useColumnFilters(searchedBusinesses, bizColumns);
  // "Sort by due date" switch: overdue first, then the nearest renewal;
  // companies with no renewal date go last.
  const filteredBusinesses = bizSortDue
    ? [...bizFiltered].sort((a, b) =>
        (a.subscriptionEnd ?? '9999').localeCompare(b.subscriptionEnd ?? '9999'))
    : bizFiltered;
  const bizNarrowed = bizFilters.active.length > 0;

  const searchedUsers = users;
  const userColumns = useMemo<FilterColumn<AdminUser>[]>(() => [
    { key: 'name',    label: 'Name',    get: u => u.name },
    { key: 'company', label: 'Company', get: u => u.businessName },
    { key: 'role',    label: 'Role',    get: u => (u.role === 'owner' ? 'Owner' : 'Staff') },
    { key: 'seat',    label: 'Seat',    get: u => (u.isFree ? 'Free seat' : 'Paid seat') },
    { key: 'status',  label: 'Status',  get: u => (u.isActive ? 'Active' : 'Disabled') },
  ], []);
  const { rows: filteredUsers, filters: userFilters } = useColumnFilters(searchedUsers, userColumns);

  const bizName = (id: string) => businesses.find(b => b.id === id)?.businessName ?? '';
  const searchedPayments = payments;
  const paymentColumns = useMemo<FilterColumn<AdminPayment>[]>(() => [
    { key: 'company', label: 'Company', get: p => bizName(p.businessId) || p.businessId },
    { key: 'user',    label: 'User',    get: p => p.userName },
    { key: 'type',    label: 'Type',    get: p => (p.kind ? PAYMENT_KIND_LABEL[p.kind] : 'Payment') },
    { key: 'currency', label: 'Currency', get: p => paymentCurrencyOf(p) },
    // Month of the payment, newest first in the menu.
    { key: 'month',   label: 'Paid Date', get: p => monthLabel(p.paidDate), sort: (x, y) => monthKey(y).localeCompare(monthKey(x)) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [businesses]);
  const { rows: filteredPayments, filters: payFilters } = useColumnFilters(searchedPayments, paymentColumns);
  // One total per currency (different currencies are never added together).
  const paymentsTotal = filteredPayments.reduce<Partial<Record<BillingCurrency, number>>>((t, p) => {
    const c = paymentCurrencyOf(p); t[c] = (t[c] ?? 0) + (p.amount ?? 0); return t;
  }, {});

  // 50 rows at a time on each table; a new search or filter goes back to page 1.
  const bizPg  = usePaging(filteredBusinesses, `${bizSortDue}|${bizFilters.stamp}`);
  const userPg = usePaging(filteredUsers, userFilters.stamp);
  const sessionColumns = useMemo<FilterColumn<AdminSession>[]>(() => [
    { key: 'user',    label: 'User',    get: x => x.userName },
    { key: 'company', label: 'Company', get: x => x.businessName },
    { key: 'status',  label: 'Status',  get: x => (x.isActive ? 'Online' : 'Offline') },
  ], []);
  const { rows: filteredSessions, filters: sessFilters } = useColumnFilters(sessions, sessionColumns);
  const sessPg = usePaging(filteredSessions, sessFilters.stamp);
  const sessWrap = useRef<HTMLDivElement>(null);
  useFitHeight(sessWrap, 56, 280, [tab, loading, filteredSessions.length > 0]);
  const payPg  = usePaging(filteredPayments, payFilters.stamp);
  // Tables fit the screen: header pinned, rows scroll inside, page numbers under.
  const bizWrap  = useRef<HTMLDivElement>(null);
  const userWrap = useRef<HTMLDivElement>(null);
  const payWrap  = useRef<HTMLDivElement>(null);
  useFitHeight(bizWrap, 56, 280, [tab, loading, filteredBusinesses.length > 0]);
  useFitHeight(userWrap, 56, 280, [tab, loading, filteredUsers.length > 0]);
  useFitHeight(payWrap, 56, 280, [tab, loading, filteredPayments.length > 0]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <AdminLayout
      activeTab={tab}
      onTabChange={setTab}
      title={TAB_META[tab].title}
      subtitle={TAB_META[tab].subtitle}
      headerActions={tab === 'dashboard' ? (
        <>
          {dashPeriod === 'custom' && <>
            <input type="date" className="tb-date" value={dashFrom} max={dashTo || undefined}
              onChange={e => setDashFrom(e.target.value)} aria-label="From" />
            <input type="date" className="tb-date" value={dashTo} min={dashFrom || undefined}
              onChange={e => setDashTo(e.target.value)} aria-label="To" />
          </>}
          <div className="tb-seg" role="tablist" aria-label="Period">
            {DASH_PERIODS.map(p => (
              <button key={p.key} type="button" role="tab" aria-selected={dashPeriod === p.key}
                className={dashPeriod === p.key ? 'on' : undefined} onClick={() => setDashPeriod(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" style={{ height: 34, boxSizing: 'border-box', width: 'auto' }}
            onClick={() => setRatesOpen(true)}
            title={liveRates ? `Today's rates, ${fmtDMY(liveRates.date)}` : 'Fallback rates: today\'s could not be loaded'}>
            Exchange Rates
          </button>
        </>
      ) : undefined}
    >
        {pageError && <div className="error-msg">{pageError}</div>}
        {pageNotice && (
          <div className="info-msg" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <span>{pageNotice}</span>
            <button type="button" className="line-item-link-btn" style={{ fontSize: 12 }} onClick={() => setPageNotice(null)}>Close</button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
            <span className="spinner" /> Loading…
          </div>
        ) : (
          <>
            {/* ── DASHBOARD ── */}
            {tab === 'dashboard' && (() => {
              // Money is kept per currency (never added across currencies) plus
              // one "≈ €" figure: a payment at the rate saved when it was
              // logged, money still owed at today's rates.
              type Money = { by: Partial<Record<BillingCurrency, number>>; eur: number };
              const zero = (): Money => ({ by: {}, eur: 0 });
              const addTo = (m: Money, amt: number, cur: BillingCurrency, eur: number) => { m.by[cur] = (m.by[cur] ?? 0) + amt; m.eur += eur; return m; };
              const money = (m: Money) => fmtBillMulti(m.by);
              const onlyEur = (m: Money) => Object.keys(m.by).every(c => c === 'EUR');
              const eurOf = (m: Money) => fmtBill(Math.round(m.eur), 'EUR');
              const payEur = (p: AdminPayment) => p.amount * (p.rateToEur ?? toEur(1, paymentCurrencyOf(p), rates));

              // Every active company's next bill, and where it stands:
              // first year never paid, renewal overdue, due within 30 days,
              // coming up in 31 to 90 days, or later in the year.
              type DueReason = 'first-year' | 'overdue' | 'due-soon' | 'upcoming';
              const billed = businesses.filter(b => b.isActive).map(b => {
                const bill = billFor(b);
                const prog = subProgress(b.subscriptionStart, b.subscriptionEnd);
                let reason: DueReason | null = null;
                if (bill.stage === 'first-year') reason = 'first-year';
                else if (prog && prog.daysLeft < 0) reason = 'overdue';
                else if (prog && prog.daysLeft <= 30) reason = 'due-soon';
                else if (prog && prog.daysLeft <= 90) reason = 'upcoming';
                return { b, bill, prog, reason };
              });
              const dueItems = billed
                .filter((x): x is typeof x & { reason: DueReason } => x.reason !== null)
                .sort((x, y) => {
                  const rank = { 'first-year': 0, overdue: 1, 'due-soon': 2, upcoming: 3 } as const;
                  return rank[x.reason] - rank[y.reason] || (x.b.subscriptionEnd ?? '').localeCompare(y.b.subscriptionEnd ?? '');
                });

              // Collected, Owed Now and Coming Up per Country (the company's
              // Country; a payment counts under its company's).
              type CountryRow = { country: string; companies: number; collected: Money; owed: Money; coming: Money };
              // The period: Collected = payments in [backFrom, today], Coming Up =
              // renewals in [today, aheadTo]; Custom uses From / To for both.
              const today = todayStr();
              const months = DASH_PERIODS.find(p => p.key === dashPeriod)?.months ?? 12;
              const isCustom = dashPeriod === 'custom';
              const backFrom = isCustom ? (dashFrom || '0000-01-01') : shiftMonths(today, -months);
              const backTo   = isCustom ? (dashTo || '9999-12-31') : today;
              const aheadFrom = isCustom ? (dashFrom > today ? dashFrom : today) : today;
              const aheadTo   = isCustom ? (dashTo || '9999-12-31') : shiftMonths(today, months);
              const NO_COUNTRY = 'No country';
              const bizById = new Map(businesses.map(b => [b.id, b]));
              const countryOf = (b?: AdminBusiness) => b?.country?.trim() || NO_COUNTRY;
              const byCountry = new Map<string, CountryRow>();
              const rowFor = (c: string) => {
                let r = byCountry.get(c);
                if (!r) { r = { country: c, companies: 0, collected: zero(), owed: zero(), coming: zero() }; byCountry.set(c, r); }
                return r;
              };
              for (const x of billed) rowFor(countryOf(x.b)).companies += 1;
              for (const p of payments) {
                const r = rowFor(countryOf(bizById.get(p.businessId)));
                if (p.paidDate && p.paidDate >= backFrom && p.paidDate <= backTo) addTo(r.collected, p.amount, paymentCurrencyOf(p), payEur(p));
              }
              const addBill = (m: Money, bill: Billing) => addTo(m, bill.totalDue, bill.currency, toEur(bill.totalDue, bill.currency, rates));
              for (const d of dueItems) {
                if (d.reason === 'first-year' || d.reason === 'overdue') addBill(rowFor(countryOf(d.b)).owed, d.bill);
              }
              for (const x of billed) {
                const end = x.b.subscriptionEnd;
                if (x.bill.stage === 'renewal' && end && end >= aheadFrom && end <= aheadTo) addBill(rowFor(countryOf(x.b)).coming, x.bill);
              }
              const countryRows = [...byCountry.values()].sort((a, b) =>
                (a.country === NO_COUNTRY ? 1 : 0) - (b.country === NO_COUNTRY ? 1 : 0) || a.country.localeCompare(b.country));
              const totalCompanies = countryRows.reduce((t, r) => t + r.companies, 0);
              const sumRows = (k: 'collected' | 'owed' | 'coming'): Money => countryRows.reduce((m, r) => {
                for (const [c, v] of Object.entries(r[k].by)) m.by[c] = (m.by[c] ?? 0) + (v ?? 0);
                m.eur += r[k].eur; return m;
              }, zero());
              const shownDue = dueItems;

              const recentPayments = [...payments]
                .sort((a, b) => (b.paidDate > a.paidDate ? 1 : -1))
                .slice(0, 8);

              function mailtoLink(b: AdminBusiness, due: number) {
                const subject = encodeURIComponent(`Subscription Renewal – ${b.businessName}`);
                const body    = encodeURIComponent(
                  `Dear ${b.ownerName},\n\nYour DiaWolt subscription for ${b.businessName} is due${b.subscriptionEnd ? ` (renewal date ${fmtDMY(b.subscriptionEnd)})` : ''}.\n\nAmount due: ${fmtBill(due, billingCurrencyOf(b))}\n\nPlease get in touch to renew your access.\n\nThank you.`
                );
                return `mailto:${b.ownerEmail}?subject=${subject}&body=${body}`;
              }

              // First Year payment for a company: opens Log Payment on its
              // owner (type and amount pre-fill from stageFor / billFor).
              function logFirstYear(b: AdminBusiness) {
                const owner = users.find(u => u.businessId === b.id && u.role === 'owner')
                           ?? users.find(u => u.businessId === b.id);
                setPaymentDefaultUserId(owner?.id ?? '');
                setRenewalBusinessId(null);
                setModal('add-payment');
              }

              const REASON: Record<DueReason, { label: string; tone: string }> = {
                'first-year': { label: 'First year unpaid', tone: 'var(--primary)' },
                overdue:      { label: 'Overdue',           tone: 'var(--danger)' },
                'due-soon':   { label: 'Due in 30 days',    tone: 'var(--warning)' },
                upcoming:     { label: 'Coming up',         tone: 'var(--text-3)' },
              };

              const panel: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: 20 };
              const panelHead: React.CSSProperties = { padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' };

              // An amount cell: each currency, with "≈ €…" under it when not €.
              const amountCell = (m: Money) => (m.eur > 0 || Object.keys(m.by).length > 0) ? <>
                {money(m)}
                {!onlyEur(m) && <span className="adm-eur">≈ {eurOf(m)}</span>}
              </> : <span style={{ opacity: 0.4 }}>—</span>;

              return (
                <>
                  {/* Payments Due: one row per company, in line with its columns. */}
                  <div style={panel}>
                    <div style={panelHead}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
                        Payments Due <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>({shownDue.length})</span>
                      </span>
                    </div>
                    {shownDue.length === 0 ? (
                      <div style={{ padding: '18px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>
                        All companies are paid up for the next 90 days.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="adm-dash-table adm-fit">
                          <colgroup>
                            <col style={{ width: '30%' }} /><col style={{ width: '14%' }} /><col style={{ width: '16%' }} />
                            <col style={{ width: '20%' }} /><col style={{ width: '20%' }} /><col style={{ width: 170 }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Company</th>
                              <th>Country</th>
                              <th>Status</th>
                              <th>Due</th>
                              <th className="num">Amount</th>
                              <th aria-label="Actions" />
                            </tr>
                          </thead>
                          <tbody>
                            {shownDue.map(({ b, bill, prog, reason }) => {
                              const r = REASON[reason];
                              const hasLogin = users.some(u => u.businessId === b.id);
                              return (
                                <tr key={b.id}>
                                  <td className="adm-due-co">
                                    <button type="button" onClick={() => { setSelectedBusiness(b); setModal('business-history'); }}>
                                      {b.businessName}
                                    </button>
                                    {b.ownerName && <span>{b.ownerName}</span>}
                                  </td>
                                  <td>{countryOf(b)}</td>
                                  <td>
                                    <span className="adm-due-pill" style={{ '--tone': r.tone } as React.CSSProperties}>{r.label}</span>
                                  </td>
                                  <td>
                                    {reason === 'first-year'
                                      ? <>Since {fmtDMY(b.subscriptionStart)}</>
                                      : <>{fmtDMY(b.subscriptionEnd)}{prog && <span className="adm-due-days" style={{ color: r.tone }}>{daysLeftText(prog.daysLeft)}</span>}</>}
                                  </td>
                                  <td className="num">
                                    <strong>{fmtBill(bill.totalDue, bill.currency)}</strong>
                                    {bill.currency !== 'EUR' && <span className="adm-eur">{eurApprox(bill.totalDue, bill.currency, rates)}</span>}
                                    {bill.extraUsers > 0 && <span className="adm-eur">{breakdownText(bill)}</span>}
                                  </td>
                                  <td className="adm-due-act">
                                    {reason === 'first-year' ? (
                                      hasLogin
                                        ? <button className="btn btn-sm btn-primary" onClick={() => logFirstYear(b)}>Log Payment</button>
                                        : <button className="btn btn-sm btn-secondary" title="Add the owner's login first, then log the payment" onClick={() => openAddUserFor(b.id)}>Add Owner</button>
                                    ) : (
                                      <button className="btn btn-sm btn-primary" onClick={() => openRenewFor(b)}>Renew</button>
                                    )}
                                    <Dropdown
                                      align="right"
                                      trigger={() => (
                                        <button type="button" className="icon-btn-round" title="More actions">
                                          <IconDots />
                                        </button>
                                      )}
                                    >
                                      <a href={mailtoLink(b, bill.totalDue)} className="dropdown-item" style={{ textDecoration: 'none' }}>Email Owner</a>
                                      <button type="button" className="dropdown-item" onClick={() => disableBusinessAccess(b)}>Disable Access</button>
                                    </Dropdown>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* By Country: who we have where, and the money, in each country's currency. */}
                  <div style={panel}>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="adm-dash-table adm-fit">
                        <colgroup>
                          <col style={{ width: '22%' }} /><col style={{ width: '12%' }} />
                          <col /><col /><col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Country</th>
                            <th className="num" title="Active companies">Companies</th>
                            <th className="num" title="Paid in the period">Collected</th>
                            <th className="num" title="Overdue and first year unpaid">Owed Now</th>
                            <th className="num" title="Renewals due in the period">Coming Up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {countryRows.length === 0 ? (
                            <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-3)', padding: 18 }}>No companies yet.</td></tr>
                          ) : countryRows.map(r => (
                            <tr key={r.country}>
                              <td className="strong">{r.country}</td>
                              <td className="num">{r.companies}</td>
                              {[r.collected, r.owed, r.coming].map((m, i) => <td key={i} className="num">{amountCell(m)}</td>)}
                            </tr>
                          ))}
                        </tbody>
                        {countryRows.length > 1 && (
                          <tfoot>
                            <tr>
                              <td>Total in €</td>
                              <td className="num">{totalCompanies}</td>
                              {[sumRows('collected'), sumRows('owed'), sumRows('coming')].map((m, i) => (
                                <td key={i} className="num">{onlyEur(m) ? eurOf(m) : `≈ ${eurOf(m)}`}</td>
                              ))}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  {/* Recent payments */}
                  <div style={panel}>
                    <div style={panelHead}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>Recent Payments</span>
                      <button className="btn btn-sm btn-secondary" onClick={() => setTab('payments')}>View All</button>
                    </div>
                    {recentPayments.length === 0 ? (
                      <div style={{ padding: '18px 16px', textAlign: 'center', color: 'var(--text-3)', fontSize: 12.5 }}>No payments yet.</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                      <table className="adm-dash-table">
                        <thead>
                          <tr>
                            <th>Date</th><th>Company</th><th>Type</th><th>User</th><th>Period</th><th className="num">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentPayments.map(p => {
                            const biz = businesses.find(b => b.id === p.businessId);
                            return (
                              <tr key={p.id}>
                                <td>{fmtDMY(p.paidDate)}</td>
                                <td className="strong">{biz?.businessName ?? '—'}</td>
                                <td><KindTag kind={p.kind} /></td>
                                <td>{p.userName}</td>
                                <td>{dmyIn(p.paidFor)}</td>
                                <td className="num">
                                  <strong>{fmtBill(p.amount, paymentCurrencyOf(p))}</strong>
                                  {paymentCurrencyOf(p) !== 'EUR' && <span className="adm-eur">{eurApprox(p.amount, paymentCurrencyOf(p), rates, p.rateToEur)}</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {/* ── BUSINESSES ── */}
            {tab === 'businesses' && (
              <>
                {/* Toolbar: search, sort by due date switch, count, Add. */}
                <div className="admin-toolbar">
                  <label className="admin-switch-label">
                    <button type="button" role="switch" aria-checked={bizSortDue}
                      className={`admin-switch${bizSortDue ? ' on' : ''}`}
                      onClick={() => setBizSortDue(v => !v)} />
                    Sort by due date
                  </label>
                  {bizNarrowed && (
                    <button type="button" className="tb-clear" onClick={() => { bizFilters.clear(); }}>Clear</button>
                  )}
                  <ColumnFilterChips filters={bizFilters} inline />
                  <span className="admin-count" style={{ marginLeft: bizFilters.active.length ? undefined : 'auto' }}>
                    {filteredBusinesses.length}{bizNarrowed ? ` of ${businesses.length}` : ''} {businesses.length === 1 ? 'company' : 'companies'}
                  </span>
                  <button className="btn btn-primary" style={{ width: 'auto', height: 34 }}
                    onClick={() => setModal('add-business')}>
                    + Add Company
                  </button>
                </div>
                <div ref={bizWrap} className="table-wrap table-fit table-fit-stats">
                  <table style={{ tableLayout: 'fixed', minWidth: 1000 }}>
                    <colgroup>
                      <col style={{ width: 180 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 190 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 130 }} />
                      <col style={{ width: 88 }} />
                      <col style={{ width: 56 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="th-filterable">Company<ColumnFilterButton filters={bizFilters} colKey="company" label="Company" /></th>
                        <th className="th-filterable">Owner<ColumnFilterButton filters={bizFilters} colKey="owner" label="Owner" /></th>
                        <th className="th-filterable">Subscription<ColumnFilterButton filters={bizFilters} colKey="renewal" label="Subscription" /></th>
                        <th className="th-filterable">Users<ColumnFilterButton filters={bizFilters} colKey="seats" label="Users" /></th>
                        <th className="th-filterable">Amount Due<ColumnFilterButton filters={bizFilters} colKey="stage" label="Amount Due" /><ColumnFilterButton filters={bizFilters} colKey="currency" label="Currency" /></th>
                        <th className="th-filterable">Status<ColumnFilterButton filters={bizFilters} colKey="status" label="Status" align="right" /></th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBusinesses.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)' }}>
                          {bizNarrowed ? 'No companies match these filters' : 'No companies yet'}
                        </td></tr>
                      )}
                      {bizPg.pageRows.map(b => {
                        const bill = billFor(b);
                        const { extraUsers, totalDue, included, activeUsers } = bill;
                        const prog = subProgress(b.subscriptionStart, b.subscriptionEnd);
                        const badge      = renewalBadge(b.subscriptionEnd);
                        const hasSubData = b.baseAmount != null || b.subscriptionEnd;
                        const openHistory = () => { setSelectedBusiness(b); setModal('business-history'); };
                        return (
                          <tr
                            key={b.id}
                            onClick={openHistory}
                            style={{ cursor: 'pointer' }}
                          >
                            <td style={{ overflow: 'hidden' }}>
                              <strong style={{ display: 'block', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.businessName}
                              </strong>
                              {(b.city || b.country) && (
                                <span style={{ color: 'var(--text-3)', fontSize: 11 }}>
                                  {b.city}{b.country ? `, ${b.country}` : ''}
                                </span>
                              )}
                            </td>
                            <td style={{ overflow: 'hidden' }}>
                              <span style={{ display: 'block', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.ownerName}
                              </span>
                              <span style={{ color: 'var(--text-3)', fontSize: 11, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {b.ownerEmail}
                              </span>
                            </td>
                            {/* Period + renewal in one cell: range and badge, a bar of
                                time used (green / amber in the last 30 days / red
                                overdue), then days left and the renewal date. */}
                            <td>
                              {prog && badge ? (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                    <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtPeriod(b.subscriptionStart, b.subscriptionEnd)}</span>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: badge.bg, color: badge.color, border: badge.border, flexShrink: 0 }}>
                                      {badge.label}
                                    </span>
                                  </div>
                                  <div style={{ margin: '5px 0 3px' }}>
                                    <ProgressBar pct={prog.pct} color={renewalTone(prog.daysLeft)} />
                                  </div>
                                  <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                                    <span style={{ fontWeight: 600, color: renewalTone(prog.daysLeft) }}>{daysLeftText(prog.daysLeft)}</span>
                                    <span style={{ color: 'var(--text-3)' }}> · {fmtDMY(b.subscriptionEnd)}</span>
                                  </span>
                                </>
                              ) : (
                                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{fmtPeriod(b.subscriptionStart, b.subscriptionEnd)}</span>
                              )}
                            </td>
                            <td>
                              <span style={{ fontSize: 12, display: 'block', whiteSpace: 'nowrap' }}>
                                <strong style={{ fontSize: 12 }}>{seatsText(activeUsers, included)}</strong>
                              </span>
                              <div style={{ margin: '5px 0 3px' }}>
                                <ProgressBar pct={(activeUsers / included) * 100} color={extraUsers > 0 ? 'var(--warning)' : 'var(--primary)'} />
                              </div>
                              <span style={{ fontSize: 11, color: extraUsers > 0 ? 'var(--warning)' : 'var(--text-3)', fontWeight: extraUsers > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                                {extraUsers > 0 ? `${activeUsers} users` : `${included - activeUsers} free seat${included - activeUsers === 1 ? '' : 's'} left`}
                              </span>
                            </td>
                            <td>
                              {hasSubData ? (
                                <>
                                  <strong style={{ fontSize: 12.5 }}>{fmtBill(totalDue, bill.currency)}</strong>
                                  {bill.currency !== 'EUR' && <span className="adm-eur">{eurApprox(totalDue, bill.currency, rates)}</span>}
                                  <span style={{ color: 'var(--text-3)', fontSize: 11, display: 'block', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                                    {STAGE_LABEL[bill.stage]}{extraUsers > 0 ? ` · ${breakdownText(bill)}` : ''}
                                  </span>
                                </>
                              ) : <span style={{ color: 'var(--text-3)' }}>—</span>}
                            </td>
                            <td>
                              <span className={`badge badge-${b.isActive ? 'active' : 'inactive'}`}>
                                {b.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <RowMenu actions={[
                                { label: 'View details', icon: <IconOpen />, onClick: openHistory },
                                { label: 'Edit', icon: <IconEdit />, onClick: () => openEditBusiness(b) },
                                b.isActive
                                  ? { label: 'Disable access', icon: <IconBan />, onClick: () => toggleBusiness(b.id, b.isActive) }
                                  : { label: 'Enable access', icon: <IconCircleCheck />, onClick: () => toggleBusiness(b.id, b.isActive) },
                                { label: 'Delete', icon: <IconTrash />, danger: true, onClick: () => deleteBusiness(b.id, b.businessName) },
                              ]} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Paging page={bizPg.page} totalPages={bizPg.totalPages} total={bizPg.total} onChange={bizPg.setPage} noun="company" />
              </>
            )}

            {/* ── USERS ── */}
            {tab === 'users' && (
              <>
                <div className="section-header">
                  <span className="admin-count">
                    {filteredUsers.length}{userFilters.active.length ? ` of ${users.length}` : ''} {users.length === 1 ? 'user' : 'users'}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ColumnFilterChips filters={userFilters} inline />
                    {(userFilters.active.length > 0) && (
                      <button type="button" className="tb-clear" onClick={() => { userFilters.clear(); }}>Clear</button>
                    )}
                    <button className="btn btn-primary" style={{ width: 'auto' }}
                      onClick={() => { setAddUserDefaultBusinessId(''); setModal('add-user'); }}
                      disabled={businesses.length === 0}>
                      + Add User
                    </button>
                  </div>
                </div>
                {businesses.length === 0 && (
                  <div className="info-msg">Add a company first before creating users.</div>
                )}
                <div ref={userWrap} className="table-wrap table-fit table-fit-stats">
                  <table style={{ tableLayout: 'fixed', minWidth: 960 }}>
                    <colgroup>
                      <col style={{ width: 160 }} />
                      <col style={{ width: 200 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 88 }} />
                      <col style={{ width: 56 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="th-filterable">Name<ColumnFilterButton filters={userFilters} colKey="name" label="Name" /></th>
                        <th>Email</th>
                        <th className="th-filterable">Company<ColumnFilterButton filters={userFilters} colKey="company" label="Company" /></th>
                        <th className="th-filterable">Role<ColumnFilterButton filters={userFilters} colKey="role" label="Role" /></th>
                        <th>Expiry</th>
                        <th className="th-filterable">Status<ColumnFilterButton filters={userFilters} colKey="status" label="Status" align="right" /></th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)' }}>
                          {userFilters.active.length ? 'Nothing matches these filters' : 'No users yet'}
                        </td></tr>
                      )}
                      {userPg.pageRows.map(u => {
                        const todayStr = new Date().toISOString().split('T')[0];
                        const expDays  = u.expiryDate
                          ? Math.ceil((new Date(u.expiryDate).getTime() - new Date(todayStr).getTime()) / 86_400_000)
                          : null;
                        const rowBg = u.isFree ? undefined
                          : expDays !== null && expDays < 0  ? 'color-mix(in srgb, var(--danger) 6%, transparent)'
                          : expDays !== null && expDays <= 30 ? 'color-mix(in srgb, var(--warning) 6%, transparent)'
                          : undefined;
                        return (
                        <tr key={u.id} style={rowBg ? { background: rowBg } : undefined}>
                          <td style={{ overflow: 'hidden' }}>
                            <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</strong>
                          </td>
                          <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-2)' }}>{u.email}</td>
                          <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>{u.businessName}</td>
                          <td>
                            <span className={`badge badge-${u.role}`}>{u.role}</span>
                            {u.isFree && <span className="badge badge-free" style={{ marginLeft: 4 }}>free</span>}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {u.expiryDate ? (
                              <>
                                <span style={{ color: expDays !== null && expDays < 0 ? 'var(--danger)' : expDays !== null && expDays <= 30 ? 'var(--warning)' : 'var(--text-2)' }}>
                                  {fmtDMY(u.expiryDate)}
                                </span>
                                {expDays !== null && expDays < 0 && <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 5, color: 'var(--danger)' }}>EXPIRED</span>}
                                {expDays !== null && expDays >= 0 && expDays <= 30 && <span style={{ fontSize: 10, fontWeight: 700, marginLeft: 5, color: 'var(--warning)' }}>{expDays}d left</span>}
                              </>
                            ) : '—'}
                          </td>
                          <td>
                            <span className={`badge badge-${u.isActive ? 'active' : 'inactive'}`}>
                              {u.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            <RowMenu actions={[
                              { label: 'Edit', icon: <IconEdit />, onClick: () => openEditUser(u) },
                              u.isActive
                                ? { label: 'Disable login', icon: <IconBan />, onClick: () => toggleUser(u.id, u.isActive) }
                                : { label: 'Enable login', icon: <IconCircleCheck />, onClick: () => toggleUser(u.id, u.isActive) },
                              { label: 'Reset two step', icon: <IconKeyReset />, title: 'For a lost or new phone: remove their authenticator app',
                                onClick: () => { setResetError(''); setResetTarget(u); } },
                              { label: 'Delete', icon: <IconTrash />, danger: true, onClick: () => deleteUser(u.id, u.name) },
                            ]} />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Paging page={userPg.page} totalPages={userPg.totalPages} total={userPg.total} onChange={userPg.setPage} noun="user" />
              </>
            )}

            {/* ── SESSIONS ── */}
            {tab === 'sessions' && (
              <>
                <div className="section-header">
                  <span className="admin-count">
                    {filteredSessions.length}{sessFilters.active.length ? ` of ${sessions.length}` : ''} {sessions.length === 1 ? 'session' : 'sessions'}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ColumnFilterChips filters={sessFilters} inline />
                    {sessFilters.active.length > 0 && (
                      <button type="button" className="tb-clear" onClick={() => sessFilters.clear()}>Clear</button>
                    )}
                    <button className="btn btn-secondary" onClick={loadAll}>Refresh</button>
                  </div>
                </div>
                <div ref={sessWrap} className="table-wrap table-fit table-fit-stats">
                  <table>
                    <thead>
                      <tr>
                        <th className="th-filterable">User<ColumnFilterButton filters={sessFilters} colKey="user" label="User" /></th>
                        <th className="th-filterable">Company<ColumnFilterButton filters={sessFilters} colKey="company" label="Company" /></th>
                        <th>Device</th>
                        <th>Login At</th>
                        <th>Last Active</th>
                        <th className="th-filterable">Status<ColumnFilterButton filters={sessFilters} colKey="status" label="Status" align="right" /></th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSessions.length === 0 && (
                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)' }}>{sessions.length ? 'Nothing matches these filters' : 'No sessions'}</td></tr>
                      )}
                      {sessPg.pageRows.map(s => (
                        <tr key={s.userId}>
                          <td>{s.userName}</td>
                          <td>{s.businessName}</td>
                          <td style={{ fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.deviceInfo}
                          </td>
                          <td style={{ fontSize: 12 }}>{fmtDate(s.loginAt)}</td>
                          <td style={{ fontSize: 12 }}>{fmtDate(s.lastActiveAt)}</td>
                          <td>
                            <span className={`badge badge-${s.isActive ? 'active' : 'inactive'}`}>
                              {s.isActive ? 'Online' : 'Offline'}
                            </span>
                          </td>
                          <td>
                            {s.isActive && (
                              <button className="btn btn-sm btn-danger"
                                onClick={() => forceLogout(s.userId)}>
                                Force Logout
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Paging page={sessPg.page} totalPages={sessPg.totalPages} total={sessPg.total} onChange={sessPg.setPage} noun="session" />
              </>
            )}

            {/* ── PAYMENTS ── */}
            {tab === 'payments' && (
              <>
                <div className="section-header">
                  <span className="admin-count">
                    {filteredPayments.length}{payFilters.active.length ? ` of ${payments.length}` : ''} {payments.length === 1 ? 'payment' : 'payments'}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ColumnFilterChips filters={payFilters} inline />
                    {(payFilters.active.length > 0) && (
                      <button type="button" className="tb-clear" onClick={() => { payFilters.clear(); }}>Clear</button>
                    )}
                    <button className="btn btn-primary" style={{ width: 'auto' }}
                      onClick={() => { setPaymentDefaultUserId(''); setRenewalBusinessId(null); setModal('add-payment'); }}
                      disabled={users.length === 0}>
                      + Log Payment
                    </button>
                  </div>
                </div>
                <div ref={payWrap} className="table-wrap table-fit table-fit-stats">
                  <table style={{ tableLayout: 'fixed', minWidth: 880 }}>
                    <colgroup>
                      <col style={{ width: 150 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 170 }} />
                      <col />
                      <col style={{ width: 56 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="th-filterable">User<ColumnFilterButton filters={payFilters} colKey="user" label="User" /></th>
                        <th className="th-filterable">Company<ColumnFilterButton filters={payFilters} colKey="company" label="Company" /></th>
                        <th className="th-filterable">Type<ColumnFilterButton filters={payFilters} colKey="type" label="Type" /></th>
                        <th className="th-filterable">Amount<ColumnFilterButton filters={payFilters} colKey="currency" label="Currency" /></th>
                        <th className="th-filterable">Paid Date<ColumnFilterButton filters={payFilters} colKey="month" label="Paid Date" /></th>
                        <th>Period</th>
                        <th>Notes</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.length === 0 && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-3)' }}>
                          {payFilters.active.length ? 'Nothing matches these filters' : 'No payments logged'}
                        </td></tr>
                      )}
                      {payPg.pageRows.map(p => {
                        const biz = businesses.find(b => b.id === p.businessId);
                        return (
                          <tr key={p.id}>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{p.userName}</td>
                            <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-2)' }}>{biz?.businessName ?? p.businessId}</td>
                            <td><KindTag kind={p.kind} /></td>
                            <td>
                              <strong style={{ color: 'var(--primary)', fontSize: 14 }}>{fmtBill(p.amount, paymentCurrencyOf(p))}</strong>
                              {paymentCurrencyOf(p) !== 'EUR' && <span className="adm-eur">{eurApprox(p.amount, paymentCurrencyOf(p), rates, p.rateToEur)}</span>}
                              {(p.extraUsers ?? 0) > 0 && p.feeAmount != null && (
                                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                                  {fmtBill(p.feeAmount, paymentCurrencyOf(p))} + {p.extraUsers} × {fmtBill(p.extraUserRate ?? 0, paymentCurrencyOf(p))}
                                </span>
                              )}
                            </td>
                            <td style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{fmtDMY(p.paidDate)}</td>
                            <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={dmyIn(p.paidFor)}>{dmyIn(p.paidFor)}</td>
                            <td style={{ fontSize: 12, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.notes ?? '—'}</td>
                            <td>
                              <RowMenu actions={[
                                { label: 'Edit', icon: <IconEdit />, onClick: () => { setSelectedPayment(p); setModal('edit-payment'); } },
                                { label: 'Delete', icon: <IconTrash />, danger: true, onClick: () => deletePayment(p.id) },
                              ]} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {filteredPayments.length > 0 && (
                      <tfoot>
                        <tr>
                          <td colSpan={3} style={{ fontWeight: 700 }}>Total · {filteredPayments.length} payment{filteredPayments.length === 1 ? '' : 's'}</td>
                          <td style={{ fontWeight: 800, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{fmtBillMulti(paymentsTotal)}</td>
                          <td colSpan={4} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <Paging page={payPg.page} totalPages={payPg.totalPages} total={payPg.total} onChange={payPg.setPage} noun="payment" />
              </>
            )}
          </>
        )}

      {ratesOpen && (
        <RatesModal
          currencies={[...new Set([...businesses.map(b => billingCurrencyOf(b)), ...payments.map(p => paymentCurrencyOf(p))])].filter(c => c !== 'EUR').sort()}
          live={liveRates} overrides={rateOverrides}
          onClose={() => setRatesOpen(false)} onSaved={setRateOverrides} />
      )}
      {(modal === 'add-business' || modal === 'edit-business') && (
        <BusinessModal editing={modal === 'edit-business' ? selectedBusiness : null} />
      )}
      {modal === 'business-history' && selectedBusiness && (() => {
        const biz = selectedBusiness;
        const bizUsers    = users.filter(u => u.businessId === biz.id);
        const bizPayments = [...payments.filter(p => p.businessId === biz.id)]
          .sort((a, b) => (b.paidDate > a.paidDate ? 1 : b.paidDate < a.paidDate ? -1 : 0));
        const totalCollected = bizPayments.reduce<Partial<Record<BillingCurrency, number>>>((t, p) => {
          const c = paymentCurrencyOf(p); t[c] = (t[c] ?? 0) + p.amount; return t;
        }, {});
        const bill = billFor(biz);
        const { extraUsers, totalDue, included, activeUsers } = bill;
        const badge      = renewalBadge(biz.subscriptionEnd);
        const hasSubData = biz.baseAmount != null || biz.subscriptionEnd;

        const TH = ({ right }: { right?: boolean }) => ({
          padding: '8px 14px', background: 'var(--surface-2)', textAlign: right ? 'right' as const : 'left' as const,
          fontSize: 11, fontWeight: 600 as const, color: 'var(--text-3)', textTransform: 'uppercase' as const,
          letterSpacing: '0.06em', borderBottom: '1px solid var(--border)',
        });

        return (
          <Modal
            open
            onClose={() => { setModal(null); setSelectedBusiness(null); }}
            size="xl"
            title={<span style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.015em' }}>{biz.businessName}</span>}
            subtitle={<span style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company Account</span>}
            headerRight={
              <span className={`badge badge-${biz.isActive ? 'active' : 'inactive'}`}>
                {biz.isActive ? 'Active' : 'Inactive'}
              </span>
            }
            footer={
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant={biz.isActive ? 'danger' : 'success'}
                    onClick={() => toggleBusiness(biz.id, biz.isActive)}>
                    {biz.isActive ? 'Disable Access' : 'Enable Access'}
                  </Button>
                  <Button size="sm" variant="secondary"
                    onClick={() => { setModal(null); setTimeout(() => openAddUserFor(biz.id), 10); }}>
                    + Add User
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" onClick={() => { setModal(null); setSelectedBusiness(null); }}>Close</Button>
                  {biz.subscriptionEnd != null && (
                    <Button
                      variant="primary"
                      disabled={badge?.label === 'On Track'}
                      title={badge?.label === 'On Track' ? 'Not due for renewal yet' : undefined}
                      onClick={() => openRenewFor(biz)}
                    >
                      Renew
                    </Button>
                  )}
                  <Button variant="primary" onClick={() => { setModal(null); setTimeout(() => openEditBusiness(biz), 10); }}>
                    Edit Company
                  </Button>
                </div>
              </div>
            }
          >
              <div style={{ minHeight: '62vh' }}>
              {/* ── Owner ── */}
              <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20 }}>
                {biz.ownerName}
                {biz.ownerEmail && <> · <span style={{ color: 'var(--text-3)' }}>{biz.ownerEmail}</span></>}
                {(biz.city || biz.country) && <> · {biz.city}{biz.country ? `, ${biz.country}` : ''}</>}
              </div>

              {/* ── Subscription + Users: two cards side by side, each with a
                  progress bar (time through the period / seats used). ── */}
              {hasSubData && (() => {
                const prog = subProgress(biz.subscriptionStart, biz.subscriptionEnd);
                const seatPct = (activeUsers / included) * 100;
                const Fig = ({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) => (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: strong ? 14 : 12.5, fontWeight: strong ? 800 : 600, color: strong ? 'var(--primary)' : 'var(--text-1)', lineHeight: 1.2 }}>{value}</div>
                  </div>
                );
                const card: React.CSSProperties = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 };
                const cardTitle: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)' };
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div style={card}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={cardTitle}>Subscription</span>
                        {badge && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: badge.bg, color: badge.color, border: badge.border }}>{badge.label}</span>}
                      </div>
                      {prog ? (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: renewalTone(prog.daysLeft) }}>
                              {daysLeftText(prog.daysLeft)}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{prog.usedDays} of {prog.totalDays} days</span>
                          </div>
                          <ProgressBar pct={prog.pct} color={renewalTone(prog.daysLeft)} height={7} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                            <span>{biz.subscriptionStart || 'Start'}</span>
                            <span>Renews {fmtDMY(biz.subscriptionEnd)}</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>No renewal date set.</div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                        <Fig label="First Year" value={fmtBill(firstYearAmountOf(biz), bill.currency)} />
                        <Fig label="Renewal" value={fmtBill(renewalAmountOf(biz), bill.currency)} />
                        <Fig label="Extra User" value={fmtBill(extraUserRateOf(biz), bill.currency)} />
                        <Fig label={bill.stage === 'first-year' ? 'First Year Due' : 'Next Renewal'} strong
                          value={<>{fmtBill(totalDue, bill.currency)}{bill.currency !== 'EUR' && <span className="adm-eur">{eurApprox(totalDue, bill.currency, rates)}</span>}</>} />
                      </div>
                    </div>

                    <div style={card}>
                      <span style={cardTitle}>Users</span>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>
                            {seatsText(activeUsers, included)}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                            {extraUsers > 0 ? `${activeUsers} users` : `${included - activeUsers} free seat${included - activeUsers === 1 ? '' : 's'} left`}
                          </span>
                        </div>
                        <ProgressBar pct={seatPct} color={extraUsers > 0 ? 'var(--warning)' : 'var(--primary)'} height={7} />
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                          {bizUsers.length - activeUsers > 0 ? `${bizUsers.length - activeUsers} disabled, not billed` : 'Only active users are billed'}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                        <Fig label="Free Users" value={Math.min(activeUsers, included)} />
                        <Fig label="Extra Users" value={extraUsers} />
                        <Fig label="Extra Cost" value={fmtBill(bill.extraAmt, bill.currency)} />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── Users ── */}
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)', marginBottom: 10 }}>
                Users ({bizUsers.length})
              </p>
              {bizUsers.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20 }}>No users yet.</p>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
                  {bizUsers.map((u, i) => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: i < bizUsers.length - 1 ? '1px solid var(--border-2)' : 'none', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 8, overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                      </div>
                      <span className={`badge badge-${u.role}`}>{u.role}</span>
                      {u.isFree && <span className="badge badge-free">free</span>}
                      <span className={`badge badge-${u.isActive ? 'active' : 'inactive'}`}>{u.isActive ? 'Active' : 'Disabled'}</span>
                      {u.expiryDate && <span style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>exp {fmtDMY(u.expiryDate)}</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* ── Payment History ── */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)' }}>
                  Payment History ({bizPayments.length})
                </p>
                {bizPayments.length > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                    Total collected: <strong style={{ color: 'var(--primary)' }}>{fmtBillMulti(totalCollected)}</strong>
                  </span>
                )}
              </div>
              {bizPayments.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No payments recorded yet.</p>
              ) : (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={TH({})}>Date</th>
                        <th style={TH({})}>Type</th>
                        <th style={TH({})}>User</th>
                        <th style={TH({})}>Period</th>
                        <th style={TH({ right: true })}>Amount</th>
                        <th style={TH({})}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bizPayments.map((p, i) => (
                        <tr key={p.id} style={{ borderBottom: i < bizPayments.length - 1 ? '1px solid var(--border-2)' : 'none' }}>
                          <td style={{ padding: '10px 14px', color: 'var(--text-2)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDMY(p.paidDate)}</td>
                          <td style={{ padding: '10px 14px' }}><KindTag kind={p.kind} /></td>
                          <td style={{ padding: '10px 14px', fontWeight: 500 }}>{p.userName}</td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-2)' }}>{dmyIn(p.paidFor)}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                            {fmtBill(p.amount, paymentCurrencyOf(p))}
                            {paymentCurrencyOf(p) !== 'EUR' && <span className="adm-eur">{eurApprox(p.amount, paymentCurrencyOf(p), rates, p.rateToEur)}</span>}
                            {(p.extraUsers ?? 0) > 0 && p.feeAmount != null && (
                              <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-3)' }}>
                                {fmtBill(p.feeAmount, paymentCurrencyOf(p))} + {p.extraUsers} × {fmtBill(p.extraUserRate ?? 0, paymentCurrencyOf(p))}
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-3)' }}>{p.notes ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
          </Modal>
        );
      })()}
      {(modal === 'add-user' || modal === 'edit-user') && (
        <UserModal editing={modal === 'edit-user' ? selectedUser : null} />
      )}
      {modal === 'add-payment' && <AddPaymentModal />}
      <ConfirmModal
        open={resetTarget !== null}
        onClose={() => { if (!resetBusy) setResetTarget(null); }}
        onConfirm={confirmResetTwoStep}
        title="Reset two step sign in?"
        message={<>
          This removes the authenticator app from <strong>{resetTarget?.name}</strong> ({resetTarget?.email}).
          They can then sign in with only their password and set it up again on their new phone.
        </>}
        confirmLabel="Reset"
        loading={resetBusy}
        error={resetError || undefined}
      />
      {modal === 'edit-payment' && selectedPayment && <EditPaymentModal editing={selectedPayment} />}
    </AdminLayout>
  );
}
